import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";
import SmsIcon from "@mui/icons-material/Sms";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import TroubleshootIcon from "@mui/icons-material/Troubleshoot";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import HideImageOutlinedIcon from "@mui/icons-material/HideImageOutlined";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import { alpha } from "@mui/material/styles";
import ENV from "../../../config/env";
import {
  actualizarDatosEquipoCentro,
  actualizarObservacionEquipoCentro,
  actualizarPlacaModemCentro,
  reasignarVehiculoModemCentro,
  cambiarEstadoEquipoCentro,
  buscarCentroEquipos,
  enviarSmsSinReporte,
  getCentroEquipoDetalle,
  listarSmsEnviados,
  listarComandosSms,
} from "../services/centroEquiposService";
import { useAuth } from "../../../context/AuthContext";
import CambiarModeloDialog from "./CambiarModeloDialog";
import DiagnosticoReporteDialog from "./DiagnosticoReporteDialog";
import { MapaUbicacion } from "./MapaRecorridoDiagnostico";
import {
  buscarEmnifyPorIccid,
  searchFlespiPrimary,
  searchFlespiSecondary,
  searchLeasecontrolUnits,
  searchWialonUnits,
} from "../../../services/modems";
import { buscarVehiculoGlobal } from "../../vehiculos/services/vehiculosService";
import {
  listarGrupos as listarGruposTrsRastreo,
  listarVehiculos as listarVehiculosTrsRastreo,
} from "../../trsrastreo/services/trsRastreoService";

// ─────────────────────────────────────────────
// Constantes
// ─────────────────────────────────────────────

const TIPOS = [
  { value: "modem", label: "MODEM" },
  { value: "chip", label: "CHIP" },
  { value: "placa", label: "PLACA" },
  { value: "radio", label: "RADIO" },
  { value: "bateria", label: "BATERIA" },
  { value: "varios", label: "VARIOS" },
  { value: "todos", label: "TODOS" },
];

const BAR_H = 30;

// Últimas búsquedas (por navegador): identificador + tipo, sin repetir.
const CLAVE_RECIENTES = "centro_equipos_busquedas_recientes";
const MAX_RECIENTES = 8;
const leerRecientes = () => {
  try {
    const l = JSON.parse(localStorage.getItem(CLAVE_RECIENTES) || "[]");
    return Array.isArray(l) ? l.slice(0, MAX_RECIENTES) : [];
  } catch { return []; }
};
const guardarRecientes = (lista) => { try { localStorage.setItem(CLAVE_RECIENTES, JSON.stringify(lista)); } catch { /* sin almacenamiento */ } };
const mismoReciente = (a, b) => a.tipo === b.tipo && String(a.buscar).toLowerCase() === String(b.buscar).toLowerCase();
const tableHeadBg = (theme) =>
  theme.palette.mode === "dark"
    ? theme.palette.background.paper
    : theme.palette.grey[100];

const BOX_THEME = {
  equipo: {
    title: {
      bgcolor: (theme) => theme.palette.mode === "dark" ? alpha(theme.palette.info.main, 0.16) : "#e8edf2",
      color: (theme) => theme.palette.mode === "dark" ? theme.palette.info.light : "#2c3a4a",
    },
    label: {
      bgcolor: (theme) => theme.palette.mode === "dark" ? alpha(theme.palette.info.main, 0.1) : "#f0f2f5",
    },
  },
  chip: {
    title: {
      bgcolor: (theme) => theme.palette.mode === "dark" ? alpha(theme.palette.primary.main, 0.18) : "#d4f3fb",
      color: (theme) => theme.palette.mode === "dark" ? theme.palette.primary.light : "#0a5c6e",
    },
    label: {
      bgcolor: (theme) => theme.palette.mode === "dark" ? alpha(theme.palette.primary.main, 0.1) : "#e5f8fd",
    },
  },
  cliente: {
    title: {
      bgcolor: (theme) => theme.palette.mode === "dark" ? alpha(theme.palette.warning.main, 0.18) : "#fff5d6",
      color: (theme) => theme.palette.mode === "dark" ? theme.palette.warning.light : "#7a5800",
    },
    label: {
      bgcolor: (theme) => theme.palette.mode === "dark" ? alpha(theme.palette.warning.main, 0.1) : "#fffaec",
    },
  },
};

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────

const normalizeId = (row) =>
  row?.id ||
  row?.identificador ||
  row?.num_sim ||
  row?.numero ||
  row?.serie ||
  row?.placa ||
  row?.id_interno;

const formatDateTime = (value) => {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}[T\s]/.test(value)) {
    return null;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/La_Paz",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(date);

  const get = (type) => parts.find((part) => part.type === type)?.value || "00";
  return `${get("year")}-${get("month")}-${get("day")} ${get("hour")}:${get("minute")}:${get("second")}`;
};

const fmt = (value) => {
  if (value === null || value === undefined || value === "") return "";
  if (Array.isArray(value)) return value.join(", ");
  const formattedDate = formatDateTime(value);
  if (formattedDate) return formattedDate;
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const first = (...values) =>
  values.find((value) => value !== null && value !== undefined && value !== "");

const formatUnixDateTime = (ts) => {
  if (!ts && ts !== 0) return "-";
  const num = Number(ts);
  if (Number.isNaN(num)) return "-";
  const ms = num < 1e12 ? num * 1000 : num;
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/La_Paz",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(new Date(ms));

  const get = (type) => parts.find((part) => part.type === type)?.value || "00";
  return `${get("year")}-${get("month")}-${get("day")} ${get("hour")}:${get("minute")}:${get("second")}`;
};

// Emnify manda fechas ISO ("2025-04-04T16:19:18.000+0000"), no unix timestamp como
// Flespi/Wialon — mismo formato de salida que formatUnixDateTime para consistencia.
const formatIsoDateTime = (iso) => {
  if (!iso) return "-";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "-";
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/La_Paz",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(date);
  const get = (type) => parts.find((part) => part.type === type)?.value || "00";
  return `${get("year")}-${get("month")}-${get("day")} ${get("hour")}:${get("minute")}:${get("second")}`;
};

const getMain = (row = {}) =>
  first(
    row.identificador,
    row.num_sim,
    row.numero,
    row.serie,
    row.placa,
    row.id,
    "-"
  );

const getEditableId = (equipo = {}, tipo = "") => {
  if (tipo === "modem") return equipo.num_sim;
  if (tipo === "chip") return equipo.numero;
  if (tipo === "radio") return equipo.serie;
  if (tipo === "bateria") return equipo.serie;
  if (tipo === "varios") return equipo.serie;
  return normalizeId(equipo);
};

const cleanSearchValue = (value) => String(value || "").trim().toUpperCase();

const rowMatchesExactSearch = (row, searchValue) => {
  const needle = cleanSearchValue(searchValue);
  if (!needle) return false;

  return [
    row?.id,
    row?.identificador,
    row?.num_sim,
    row?.numero,
    row?.serie,
    row?.placa,
    row?.id_interno,
    row?.emai,
  ].some((value) => cleanSearchValue(value) === needle);
};

// ─────────────────────────────────────────────
// Componentes compactos
// ─────────────────────────────────────────────

function FieldRows({ left = [], right = [], labelSx = {} }) {
  const maxRows = Math.max(left.length, right.length);
  const renderValue = (field = {}) => (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexWrap: "wrap" }}>
      <Box component="span" sx={{ flex: "1 1 auto", minWidth: "3em", wordBreak: "break-word" }}>
        {field.link ? (
          <a
            href={field.link}
            style={{
              color: "inherit",
              textDecoration: "none",
              fontWeight: 600,
            }}
          >
            {fmt(field.value) || "-"}
          </a>
        ) : (
          fmt(field.value) || "-"
        )}
      </Box>
      {field.action ? <Box component="span" sx={{ flexShrink: 0 }}>{field.action}</Box> : null}
    </Stack>
  );

  return (
    <Table
      size="small"
      sx={{
        "& .MuiTableCell-root": {
          py: "2px",
          px: "5px",
          fontSize: 11.5,
          borderColor: "divider",
          verticalAlign: "top",
        },
      }}
    >
      <TableBody>
        {Array.from({ length: maxRows }).map((_, index) => {
          const l = left[index] || {};
          const r = right[index] || {};

          return (
            <TableRow key={index}>
              <TableCell
                sx={{
                  width: 98,
                  fontWeight: 800,
                  fontSize: 11,
                  whiteSpace: "nowrap",
                  ...labelSx,
                  bgcolor: l.label ? labelSx.bgcolor : "transparent",
                }}
              >
                {l.label || ""}
              </TableCell>

              <TableCell sx={{ wordBreak: "break-word" }}>
                {renderValue(l)}
              </TableCell>

              {right.length > 0 && (
                <>
                  <TableCell
                    sx={{
                      width: 92,
                      fontWeight: 800,
                      fontSize: 11,
                      whiteSpace: "nowrap",
                      ...labelSx,
                      bgcolor: r.label ? labelSx.bgcolor : "transparent",
                    }}
                  >
                    {r.label || ""}
                  </TableCell>

                  <TableCell sx={{ wordBreak: "break-word" }}>
                    {renderValue(r)}
                  </TableCell>
                </>
              )}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

function InfoBox({ title, theme = BOX_THEME.equipo, children }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        height: "100%",
        overflow: "hidden",
        borderRadius: 1,
      }}
    >
      <Typography
        align="center"
        sx={{
          py: "3px",
          fontSize: 11.5,
          fontWeight: 800,
          letterSpacing: "0.03em",
          borderBottom: "1px solid",
          borderColor: "divider",
          ...theme.title,
        }}
      >
        {title}
      </Typography>

      {children}
    </Paper>
  );
}

function SmsCommandForm({ chip = {}, modem = {}, onToast, onError, compact = false }) {
  const numero = first(chip.numero, chip.num);
  const modelo = modem?.modelo || "";
  const modemId = first(modem.num_sim, modem.emai, modem.id, modem.ID_A);
  const [commands, setCommands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("form");
  const [selected, setSelected] = useState("");
  const [custom, setCustom] = useState("");
  const [sentLoading, setSentLoading] = useState(false);
  const [sentRows, setSentRows] = useState([]);
  const [sentPage, setSentPage] = useState(1);
  const [sentTotal, setSentTotal] = useState(0);
  const sentLimit = 10;

  useEffect(() => {
    let active = true;
    const loadCommands = async () => {
      if (!numero) {
        setCommands([]);
        setSelected("");
        return;
      }

      try {
        setLoading(true);
        const { data } = await listarComandosSms({ modelo });
        const rows = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
        if (!active) return;
        setCommands(rows);
        // Por defecto el Reset: el texto real del comando varía por fabricante
        // (cpureset, RESET#, RESTART#, AT+GTRTO=..., !R3,70,0, etc. — ver tabla
        // `comandos`), lo único consistente entre modelos es descripcion='Reset'.
        const reset = rows.find((cmd) => String(cmd.descripcion || "").trim().toLowerCase() === "reset");
        setSelected(reset?.comando || rows[0]?.comando || "");
      } catch (e) {
        if (!active) return;
        setCommands([]);
        setSelected("");
        onError?.(e?.response?.data?.message || e?.message || "No se pudieron cargar comandos SMS");
      } finally {
        if (active) setLoading(false);
      }
    };

    loadCommands();
    return () => {
      active = false;
    };
  }, [modelo, numero, onError]);

  useEffect(() => {
    if (open) setTab("form");
  }, [numero, open]);

  const loadSent = useCallback(async (page = 1) => {
    if (!modemId) {
      setSentRows([]);
      setSentTotal(0);
      return;
    }

    try {
      setSentLoading(true);
      const { data } = await listarSmsEnviados({ modem: modemId, limit: sentLimit, page });
      const rows = Array.isArray(data?.items) ? data.items : Array.isArray(data?.data?.items) ? data.data.items : [];
      setSentRows(rows);
      setSentTotal(Number(data?.total ?? data?.data?.total ?? rows.length));
      setSentPage(Number(data?.page ?? data?.data?.page ?? page));
    } catch (e) {
      setSentRows([]);
      setSentTotal(0);
      onError?.(e?.response?.data?.message || e?.message || "No se pudo cargar el listado de SMS");
    } finally {
      setSentLoading(false);
    }
  }, [modemId, onError]);

  useEffect(() => {
    if (open && tab === "list") loadSent(sentPage);
  }, [open, tab, sentPage, loadSent]);

  const send = async () => {
    const comando = selected || custom;
    if (!numero || !comando) return;

    try {
      setSending(true);
      onError?.("");
      await enviarSmsSinReporte({
        numero,
        comando,
        comandoPersonalizado: custom,
      });
      setCustom("");
      if (tab === "list") loadSent(1);
      onToast?.({ open: true, message: "Comando SMS enviado", severity: "success" });
    } catch (e) {
      onError?.(e?.response?.data?.message || e?.response?.data?.error || e?.message || "No se pudo enviar el SMS");
    } finally {
      setSending(false);
    }
  };

  if (!numero) return null;

  const totalPages = Math.max(1, Math.ceil(sentTotal / sentLimit));

  return (
    <Box>
      {compact ? (
        // En tablas angostas (chip relacionado) solo el ícono, para que nunca quede fuera de la vista.
        <Tooltip title="Enviar / ver SMS">
          <IconButton size="small" color="primary" aria-label="SMS" onClick={() => setOpen(true)} sx={{ border: "1px solid", borderColor: "primary.main", borderRadius: 1, p: 0.4 }}>
            <SmsIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      ) : (
        <Button
          size="small"
          variant="outlined"
          startIcon={<SmsIcon fontSize="small" />}
          onClick={() => setOpen(true)}
          sx={{ minHeight: 26, height: 26, fontSize: 11, textTransform: "none" }}
        >
          SMS
        </Button>
      )}

      <Dialog open={open} onClose={() => !sending && setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontSize: 14, fontWeight: 800, py: 1 }}>
          SMS del modem {fmt(modemId)}
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <Tabs
            value={tab}
            onChange={(_, value) => {
              setTab(value);
              if (value === "list") loadSent(1);
            }}
            sx={{ minHeight: 34, "& .MuiTab-root": { minHeight: 34, fontSize: 12, textTransform: "none" } }}
          >
            <Tab value="form" label="Enviar comando" />
            <Tab value="list" label="Comandos enviados" />
          </Tabs>

          {tab === "form" && (
            <Stack spacing={0.8} sx={{ pt: 1 }}>
              <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>
                Chip: <b>{numero}</b>
              </Typography>
              <Select
                size="small"
                value={selected}
                onChange={(e) => setSelected(e.target.value)}
                disabled={loading || sending || !commands.length}
                displayEmpty
                sx={{ height: 30, fontSize: 11.5 }}
              >
                <MenuItem value="" sx={{ fontSize: 11.5 }}>
                  {loading ? "Cargando..." : "Seleccione comando"}
                </MenuItem>
                {commands.map((cmd) => (
                  <MenuItem key={cmd.id || `${cmd.descripcion}-${cmd.comando}`} value={cmd.comando} sx={{ fontSize: 11.5 }}>
                    {cmd.descripcion || "Comando"} - {cmd.comando}
                  </MenuItem>
                ))}
              </Select>
              <TextField
                size="small"
                value={custom}
                onChange={(e) => setCustom(e.target.value)}
                placeholder="Comando personalizado"
                multiline
                minRows={3}
                inputProps={{ maxLength: 250 }}
                sx={{ "& .MuiInputBase-input": { fontSize: 11.5 } }}
              />
            </Stack>
          )}

          {tab === "list" && (
            <Box sx={{ pt: 1 }}>
              {sentLoading && <LinearProgress sx={{ mb: 0.75 }} />}
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360 }}>
                <Table size="small" stickyHeader sx={{ "& .MuiTableCell-root": { py: 0.45, px: 0.7, fontSize: 11 } }}>
                  <TableHead>
                    <TableRow>
                      {["Fecha", "Chip", "Comando", "Usuario", "Estado"].map((col) => (
                        <TableCell key={col} sx={{ fontWeight: 800, bgcolor: tableHeadBg }}>
                          {col}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {!sentRows.length && (
                      <TableRow>
                        <TableCell colSpan={5} sx={{ color: "text.secondary", fontStyle: "italic" }}>
                          Sin comandos enviados.
                        </TableCell>
                      </TableRow>
                    )}
                    {sentRows.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell>{fmt(row.fecha_sort)}</TableCell>
                        <TableCell>{fmt(row.chip)}</TableCell>
                        <TableCell title={fmt(row.comando)}>{fmt(row.comando)}</TableCell>
                        <TableCell>{fmt(row.usuario)}</TableCell>
                        <TableCell>{fmt(row.estado)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1} sx={{ mt: 0.75 }}>
                <Typography sx={{ fontSize: 11.5 }}>
                  {sentTotal} registros | Pagina {sentPage} de {totalPages}
                </Typography>
                <Button size="small" disabled={sentPage <= 1 || sentLoading} onClick={() => setSentPage((p) => Math.max(1, p - 1))}>
                  Anterior
                </Button>
                <Button size="small" disabled={sentPage >= totalPages || sentLoading} onClick={() => setSentPage((p) => p + 1)}>
                  Siguiente
                </Button>
              </Stack>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5 }}>
          <Button size="small" onClick={() => setOpen(false)} disabled={sending}>
            Cerrar
          </Button>
          {tab === "form" && (
            <Button
              size="small"
              variant="contained"
              startIcon={sending ? <CircularProgress size={12} color="inherit" /> : <SmsIcon fontSize="small" />}
              onClick={send}
              disabled={sending || !numero || (!selected && !custom)}
            >
              Enviar SMS
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function ChipsRelacionadosTable({ chips = [], modem = {}, onToast, onError, onSelectChip }) {
  if (!Array.isArray(chips) || !chips.length) {
    return <FieldRows left={buildChipFields([])} labelSx={BOX_THEME.chip.label} />;
  }

  return (
    <Box sx={{ overflowX: "auto" }}>
    <Table
      size="small"
      sx={{
        "& .MuiTableCell-root": {
          py: "3px",
          px: "5px",
          fontSize: 11,
          borderColor: "rgba(224,224,224,0.75)",
        },
      }}
    >
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: 800, bgcolor: BOX_THEME.chip.label.bgcolor }}>Numero</TableCell>
          <TableCell sx={{ fontWeight: 800, bgcolor: BOX_THEME.chip.label.bgcolor }}>Modelo</TableCell>
          <TableCell sx={{ fontWeight: 800, bgcolor: BOX_THEME.chip.label.bgcolor }}>Estado</TableCell>
          <TableCell align="center" sx={{ fontWeight: 800, bgcolor: BOX_THEME.chip.label.bgcolor, width: 46 }}>SMS</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {chips.map((chip, index) => {
          const numero = first(chip.numero, chip.num);
          return (
          <TableRow key={numero || index} hover>
            <TableCell title={fmt(numero)}>
              {numero ? (
                <Typography
                  component="span"
                  onClick={() => onSelectChip?.(numero)}
                  sx={{ fontSize: 11, color: "#1976d2", cursor: "pointer", textDecoration: "underline", fontWeight: 700 }}
                >
                  {numero}
                </Typography>
              ) : fmt(numero)}
            </TableCell>
            <TableCell title={fmt(chip.modelo)}>{fmt(chip.modelo)}</TableCell>
            <TableCell title={fmt(chip.estado)}>{fmt(chip.estado)}</TableCell>
            <TableCell align="center">
              <SmsCommandForm chip={chip} modem={modem} onToast={onToast} onError={onError} compact />
            </TableCell>
          </TableRow>
          );
        })}
      </TableBody>
    </Table>
    </Box>
  );
}

function ModemsRelacionadosTable({ modems = [], onSelectModem }) {
  if (!Array.isArray(modems) || !modems.length) {
    return <FieldRows left={buildModemFields([])} labelSx={BOX_THEME.chip.label} />;
  }

  return (
    <Table
      size="small"
      sx={{
        "& .MuiTableCell-root": {
          py: "3px",
          px: "5px",
          fontSize: 11,
          borderColor: "rgba(224,224,224,0.75)",
        },
      }}
    >
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: 800, bgcolor: BOX_THEME.chip.label.bgcolor }}>Num sim</TableCell>
          <TableCell sx={{ fontWeight: 800, bgcolor: BOX_THEME.chip.label.bgcolor }}>Estado</TableCell>
          <TableCell sx={{ fontWeight: 800, bgcolor: BOX_THEME.chip.label.bgcolor }}>Marca / Modelo</TableCell>
          <TableCell sx={{ fontWeight: 800, bgcolor: BOX_THEME.chip.label.bgcolor }}>Ciudad</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {modems.map((modem, index) => (
          <TableRow key={modem.num_sim || index} hover>
            <TableCell>
              {modem.num_sim ? (
                <Typography
                  component="span"
                  onClick={() => onSelectModem?.(modem.num_sim)}
                  sx={{
                    fontSize: 11,
                    color: "#1976d2",
                    cursor: "pointer",
                    textDecoration: "underline",
                    fontWeight: 700,
                  }}
                >
                  {modem.num_sim}
                </Typography>
              ) : (
                fmt(modem.num_sim)
              )}
            </TableCell>
            <TableCell title={fmt(modem.estado)}>{fmt(modem.estado)}</TableCell>
            <TableCell title={fmt([modem.marca, modem.modelo].filter(Boolean).join(" / "))}>
              {fmt([modem.marca, modem.modelo].filter(Boolean).join(" / "))}
            </TableCell>
            <TableCell title={fmt(modem.ciudad)}>{fmt(modem.ciudad)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function PlatformValue({ label, value }) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 10.8, color: "text.secondary", whiteSpace: "nowrap" }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 10.8, fontWeight: 700, textAlign: "right", minWidth: 0 }} noWrap title={fmt(value)}>
        {value || "-"}
      </Typography>
    </Stack>
  );
}

// Estado del último dato de una plataforma: verde si reportó hace poco, naranja si lleva horas sin reportar y rojo si
// pasó más de un día. Se muestra en la cabecera de la tarjeta para ver de un vistazo en cuál está reportando el equipo.
const estadoReporte = (ts) => {
  if (!ts) return { color: "text.disabled", texto: "Sin fecha de reporte" };
  const seg = Math.max(0, Date.now() / 1000 - Number(ts));
  const min = Math.floor(seg / 60);
  const hace = min < 1 ? "hace segundos"
    : min < 60 ? `hace ${min} min`
      : min < 60 * 48 ? `hace ${Math.floor(min / 60)} h ${min % 60 ? `${min % 60} min` : ""}`.trim()
        : `hace ${Math.floor(min / 1440)} días`;
  if (min <= 30) return { color: "success.main", texto: `Reportando · ${hace}` };
  if (min <= 1440) return { color: "warning.main", texto: `Sin reportar ${hace.replace("hace ", "")}` };
  return { color: "error.main", texto: `Sin reportar ${hace.replace("hace ", "")}` };
};

function PlatformCard({ title, data, keyName, color, bg, rows = [], ts, onOpen, onDiagnosticar }) {
  const estado = ts !== undefined ? estadoReporte(ts) : null;
  // La fecha del último dato ya va en el estado de la cabecera; el detalle la sigue mostrando completa.
  const filas = estado ? rows.filter((row) => row.label !== "Ultimo") : rows;
  const ultimo = rows.find((row) => row.label === "Ultimo")?.value;
  return (
    <Paper
      variant="outlined"
      onClick={() => data && onOpen?.()}
      sx={{
        borderRadius: 1,
        overflow: "hidden",
        borderTop: `3px solid ${color}`,
        bgcolor: "background.paper",
        display: "flex",
        flexDirection: "column",
        cursor: data ? "pointer" : "default",
        transition: "box-shadow 120ms ease, border-color 120ms ease",
        "&:hover": data ? { boxShadow: 3, borderColor: alpha(color, 0.6) } : {},
      }}
    >
      <Box
        sx={{
          px: 1,
          pt: 0.75,
          pb: 0.7,
          bgcolor: (theme) => (data ? (theme.palette.mode === "dark" ? alpha(color, 0.14) : bg) : "transparent"),
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography sx={{ fontSize: 10.5, fontWeight: 900, color, textTransform: "uppercase", letterSpacing: 0.4, flex: 1, minWidth: 0 }} noWrap>
            {title}
          </Typography>
          {data && onDiagnosticar && (
            <Tooltip title={`Diagnosticar el reporte en ${title}`}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<TroubleshootIcon sx={{ fontSize: "14px !important" }} />}
                onClick={(e) => { e.stopPropagation(); onDiagnosticar(); }}
                sx={{
                  minHeight: 22, height: 22, py: 0, px: 0.75, fontSize: 10.5, fontWeight: 800, textTransform: "none",
                  color, borderColor: alpha(color, 0.45), bgcolor: "background.paper",
                  "&:hover": { borderColor: color, bgcolor: "background.paper" },
                  "& .MuiButton-startIcon": { mr: 0.35 },
                }}
              >
                Diagnosticar
              </Button>
            </Tooltip>
          )}
        </Stack>
        {data && (
          <>
            <Typography sx={{ fontSize: 14, fontWeight: 850, mt: 0.2, lineHeight: 1.3 }} noWrap title={fmt(data?.[keyName])}>
              {fmt(data?.[keyName]) || "-"}
            </Typography>
            {estado && (
              <Stack direction="row" alignItems="center" spacing={0.6} sx={{ mt: 0.3 }} title={ultimo ? `Último dato: ${ultimo}` : undefined}>
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: estado.color, flexShrink: 0 }} />
                <Typography sx={{ fontSize: 11, fontWeight: 800, color: estado.color }} noWrap>
                  {estado.texto}
                </Typography>
                {ultimo && (
                  <Typography sx={{ fontSize: 10.5, color: "text.secondary", fontVariantNumeric: "tabular-nums" }} noWrap>
                    · {ultimo}
                  </Typography>
                )}
              </Stack>
            )}
          </>
        )}
      </Box>
      {data ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
            columnGap: 1.5,
            rowGap: 0.6,
            px: 1,
            py: 0.8,
            borderTop: "1px solid",
            borderColor: "divider",
            flex: 1,
          }}
        >
          {filas.map((row) => (
            <Box key={row.label} sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 9.8, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.3, lineHeight: 1.3 }} noWrap>
                {row.label}
              </Typography>
              <Typography sx={{ fontSize: 11.5, fontWeight: 700, lineHeight: 1.35 }} noWrap title={fmt(row.value)}>
                {row.value || "-"}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : (
        <Typography sx={{ fontSize: 10.8, color: "text.disabled", fontStyle: "italic", px: 1, pb: 1 }}>
          Sin conexion
        </Typography>
      )}
    </Paper>
  );
}

const toCoordNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const getPlatformCoords = (data = {}) => {
  const telemetry = data?.telemetry || {};
  const pos = telemetry.position || data?.position || data?.pos || data?.lmsg?.pos || data?.lmsg?.p?.pos || {};

  const lat = first(
    telemetry["position.latitude"],
    telemetry["position.lat"],
    pos.latitude,
    pos.lat,
    pos.y,
    data?.lat,
    data?.latitude,
    data?.latitud
  );
  const lng = first(
    telemetry["position.longitude"],
    telemetry["position.lon"],
    telemetry["position.lng"],
    pos.longitude,
    pos.lon,
    pos.lng,
    pos.x,
    data?.lon,
    data?.lng,
    data?.longitude,
    data?.longitud
  );

  const latitude = toCoordNumber(lat);
  const longitude = toCoordNumber(lng);
  if (latitude === null || longitude === null) return null;
  if (Math.abs(latitude) > 90 || Math.abs(longitude) > 180) return null;
  return { latitude, longitude };
};

const MESES_ES = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];
const formatMesAnio = (yyyyMmDd) => {
  if (!yyyyMmDd) return "-";
  const [anio, mes] = String(yyyyMmDd).split("-");
  const nombreMes = MESES_ES[Number(mes) - 1];
  return nombreMes ? `${nombreMes} ${anio}` : yyyyMmDd;
};
const formatMB = (value) => (value === undefined || value === null ? "-" : `${Number(value).toFixed(2)} MB`);

// "48:57:54" (HH puede pasar de 24) -> "2 días 00:57:54", igual formato que usa el
// panel web de Emnify para la duracion del contexto PDP activo.
const formatDuracionHHMMSS = (hhmmss) => {
  if (!hhmmss) return "-";
  const partes = String(hhmmss).split(":").map(Number);
  if (partes.length !== 3 || partes.some((n) => Number.isNaN(n))) return hhmmss;
  const [h, m, s] = partes;
  const dias = Math.floor(h / 24);
  const pad = (n) => String(n).padStart(2, "0");
  const tiempo = `${pad(h % 24)}:${pad(m)}:${pad(s)}`;
  return dias > 0 ? `${dias} día${dias === 1 ? "" : "s"} ${tiempo}` : tiempo;
};

// Bandera a partir del codigo ISO de 2 letras del pais (ej "bo" -> 🇧🇴), via los
// caracteres Unicode "regional indicator symbol".
const isoToFlagEmoji = (iso2) => {
  if (!iso2 || iso2.length !== 2) return "";
  const codePoints = [...iso2.toUpperCase()].map((c) => 127397 + c.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

const formatGrupos = (grupos) => {
  if (!Array.isArray(grupos) || !grupos.length) return "-";
  return grupos.map((g) => g.nm || g.id).join(", ");
};

const formatUsuarios = (usuarios) => {
  if (!Array.isArray(usuarios) || !usuarios.length) return "-";
  return usuarios.map((u) => u.nm || u.id).join(", ");
};

// Filas extra para YPFB Nacional/Internacional — mismos campos que ya lee
// BusquedaUnidadTab.jsx (getRazon/getVelocidad/getPuntoCercano) desde `extras`, el
// objeto crudo que devuelve la búsqueda global sin normalizar.
const ypfbRows = (item) => {
  const extras = item?.extras || {};
  const cliente = extras.vehAlias || extras.CLINOMBRE || `${extras.nombre_normal || ""} ${extras.descripcion_normal || ""}`.trim();
  const velocidad = extras.vehVelocidadU;
  const puntoCercano = extras.vehPuntoCercanoU || extras.VEHPUNTOCERCANOu;
  return [
    { label: "Alias", value: item?.alias },
    { label: "Cliente", value: cliente || undefined },
    { label: "Cuadrilla", value: extras.cuadrilla },
    { label: "Velocidad", value: velocidad != null ? `${velocidad} km/h` : undefined },
    { label: "Punto cercano", value: puntoCercano },
    { label: "Ultimo", value: item?.fecha_ultimo_registro },
  ];
};

// gps_valid según protocolo: Coban "F" (señal completa = válida) / "L" (perdida); NMEA "A" / "V"; o booleano.
const gpsValidoTexto = (v) => {
  if (v === true || ["F", "A", "T", "TRUE", "1"].includes(String(v).trim().toUpperCase())) return "SI";
  if (v === false || ["L", "V", "FALSE", "0"].includes(String(v).trim().toUpperCase())) return "NO";
  return "-";
};

const fechaLaPazAUnix = (valor) => {
  const m = /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})(:\d{2})?/.exec(String(valor || ""));
  if (!m) return null;
  const ms = Date.parse(`${m[1]}T${m[2]}${m[3] || ":00"}-04:00`);
  return Number.isFinite(ms) ? ms / 1000 : null;
};

const PLATFORMS_EMPTY = {
  flespi1: null, flespi2: null, wialon: null, leasecontrol: null,
  internacional: null, nacional: null, rastreoBo: null,
};

const flattenPlatformData = (data) => {
  const rows = [];
  const walk = (obj, prefix = "") => {
    Object.entries(obj || {}).forEach(([key, value]) => {
      const path = prefix ? `${prefix}.${key}` : key;
      if (value && typeof value === "object" && !Array.isArray(value)) {
        walk(value, path);
        return;
      }
      rows.push({ key: path, value: Array.isArray(value) ? value.join(", ") : value });
    });
  };
  walk(data);
  return rows;
};

const copiarTexto = (texto) => {
  try { navigator.clipboard?.writeText(String(texto)); } catch { /* sin portapapeles */ }
};

// Detalle de una tarjeta de plataforma: datos principales, última ubicación en mapa (con capas) y todos los datos crudos
// que devuelve la plataforma, con buscador.
function PlatformDetailDialog({ card, ts, onClose, onDiagnosticar }) {
  const [filtro, setFiltro] = useState("");
  const [copiado, setCopiado] = useState("");
  useEffect(() => { setFiltro(""); }, [card]);

  const coords = getPlatformCoords(card?.data);
  const estado = ts !== undefined ? estadoReporte(ts) : null;
  const ultimo = (card?.rows || []).find((row) => row.label === "Ultimo")?.value;
  const nombre = fmt(card?.data?.[card?.keyName]) || "-";
  const crudos = useMemo(() => flattenPlatformData(card?.data), [card]);
  const crudosFiltrados = useMemo(() => {
    const q = filtro.trim().toLowerCase();
    if (!q) return crudos;
    return crudos.filter((r) => r.key.toLowerCase().includes(q) || String(r.value ?? "").toLowerCase().includes(q));
  }, [crudos, filtro]);
  const color = card?.color || "#1976d2";

  const copiar = (texto, que) => {
    copiarTexto(texto);
    setCopiado(que);
    setTimeout(() => setCopiado(""), 1500);
  };

  return (
    <Dialog open={Boolean(card)} onClose={onClose} maxWidth="md" fullWidth>
      <Box sx={{ borderTop: `4px solid ${color}`, bgcolor: (theme) => (theme.palette.mode === "dark" ? alpha(color, 0.14) : card?.bg), px: 2, pt: 1.25, pb: 1.25 }}>
        <Stack direction="row" alignItems="flex-start" spacing={1}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 900, color, textTransform: "uppercase", letterSpacing: 0.4 }}>
              {card?.title || "Plataforma"}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography sx={{ fontSize: 18, fontWeight: 850, lineHeight: 1.3 }} noWrap title={nombre}>
                {nombre}
              </Typography>
              <Tooltip title={copiado === "nombre" ? "Copiado" : "Copiar"}>
                <IconButton size="small" onClick={() => copiar(nombre, "nombre")}><ContentCopyIcon sx={{ fontSize: 15 }} /></IconButton>
              </Tooltip>
            </Stack>
            {estado && (
              <Stack direction="row" alignItems="center" spacing={0.6} sx={{ mt: 0.2 }}>
                <Box sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: estado.color, flexShrink: 0 }} />
                <Typography sx={{ fontSize: 12, fontWeight: 800, color: estado.color }}>{estado.texto}</Typography>
                {ultimo && <Typography sx={{ fontSize: 11.5, color: "text.secondary", fontVariantNumeric: "tabular-nums" }}>· {ultimo}</Typography>}
              </Stack>
            )}
          </Box>
          <IconButton size="small" onClick={onClose} aria-label="Cerrar"><CloseIcon fontSize="small" /></IconButton>
        </Stack>
      </Box>

      <DialogContent dividers sx={{ px: 2, py: 1.5 }}>
        <Stack spacing={1.5}>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", columnGap: 2, rowGap: 1 }}>
            {(card?.rows || []).filter((row) => !(estado && row.label === "Ultimo")).map((row) => (
              <Box key={row.label} sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 10, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.3 }} noWrap>{row.label}</Typography>
                <Typography sx={{ fontSize: 12.5, fontWeight: 700 }} noWrap title={fmt(row.value)}>{row.value || "-"}</Typography>
              </Box>
            ))}
          </Box>

          {coords && (
            <Paper variant="outlined" sx={{ overflow: "hidden", borderRadius: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1, py: 0.5, bgcolor: tableHeadBg, borderBottom: "1px solid", borderColor: "divider" }}>
                <Typography sx={{ fontSize: 12, fontWeight: 900 }}>Última ubicación</Typography>
                <Typography sx={{ fontSize: 11, color: "text.secondary", fontVariantNumeric: "tabular-nums", flex: 1 }} noWrap>
                  {coords.latitude.toFixed(6)}, {coords.longitude.toFixed(6)}
                </Typography>
                <Tooltip title={copiado === "coords" ? "Copiado" : "Copiar coordenadas"}>
                  <IconButton size="small" onClick={() => copiar(`${coords.latitude},${coords.longitude}`, "coords")}><ContentCopyIcon sx={{ fontSize: 15 }} /></IconButton>
                </Tooltip>
                <Button
                  size="small"
                  component="a"
                  href={`https://www.google.com/maps?q=${coords.latitude},${coords.longitude}`}
                  target="_blank"
                  rel="noreferrer"
                  startIcon={<MapOutlinedIcon sx={{ fontSize: "16px !important" }} />}
                  sx={{ textTransform: "none", fontSize: 11.5, fontWeight: 800, py: 0.2 }}
                >
                  Google Maps
                </Button>
              </Stack>
              <MapaUbicacion latitud={coords.latitude} longitud={coords.longitude} color={color} titulo={nombre} height={280} />
            </Paper>
          )}

          <Paper variant="outlined" sx={{ overflow: "hidden", borderRadius: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1, py: 0.5, bgcolor: tableHeadBg, borderBottom: "1px solid", borderColor: "divider" }}>
              <Typography sx={{ fontSize: 12, fontWeight: 900 }}>Datos de la plataforma</Typography>
              <Typography sx={{ fontSize: 11, color: "text.secondary", flex: 1 }}>
                {filtro ? `${crudosFiltrados.length} de ${crudos.length}` : crudos.length}
              </Typography>
              <TextField
                size="small"
                placeholder="Buscar dato o valor"
                value={filtro}
                onChange={(e) => setFiltro(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16 }} /></InputAdornment>,
                  sx: { fontSize: 12, height: 28, bgcolor: "background.paper" },
                }}
                sx={{ width: 220 }}
              />
            </Stack>
            <TableContainer sx={{ maxHeight: 340 }}>
              <Table size="small" stickyHeader sx={{ "& .MuiTableCell-root": { py: 0.4, px: 1, fontSize: 11.5 } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 900, bgcolor: tableHeadBg, width: "40%" }}>Dato</TableCell>
                    <TableCell sx={{ fontWeight: 900, bgcolor: tableHeadBg }}>Valor</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {crudosFiltrados.map((row) => (
                    <TableRow key={row.key} hover>
                      <TableCell sx={{ fontWeight: 700, color: "text.secondary", fontFamily: "monospace", fontSize: "11px !important" }}>{row.key}</TableCell>
                      <TableCell sx={{ wordBreak: "break-word", fontVariantNumeric: "tabular-nums" }}>{fmt(row.value) || "-"}</TableCell>
                    </TableRow>
                  ))}
                  {!crudosFiltrados.length && (
                    <TableRow>
                      <TableCell colSpan={2} sx={{ textAlign: "center", color: "text.secondary", fontStyle: "italic" }}>
                        Ningún dato coincide con “{filtro}”.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1 }}>
        {onDiagnosticar && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<TroubleshootIcon />}
            onClick={() => { onClose(); onDiagnosticar(); }}
            sx={{ textTransform: "none", fontWeight: 800 }}
          >
            Diagnosticar reporte
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button size="small" variant="contained" onClick={onClose} sx={{ textTransform: "none", fontWeight: 800 }}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ModemPlatformsPanel({ modem = {}, onError }) {
  const [platforms, setPlatforms] = useState(PLATFORMS_EMPTY);
  const [gruposTrs, setGruposTrs] = useState([]);
  const [vehiculoTrs, setVehiculoTrs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailCard, setDetailCard] = useState(null);
  const [diagnostico, setDiagnostico] = useState(null); // plataforma que se está diagnosticando
  const searchKey = useMemo(() => [modem?.num_sim, modem?.emai].filter(Boolean).join("|"), [modem?.num_sim, modem?.emai]);

  useEffect(() => {
    let active = true;
    const searchValues = [modem?.num_sim, modem?.emai].filter(Boolean);

    if (!searchValues.length) {
      setPlatforms(PLATFORMS_EMPTY);
      return undefined;
    }

    const load = async () => {
      try {
        setPlatforms(PLATFORMS_EMPTY);
        setGruposTrs([]);
        setVehiculoTrs(null);
        setDetailCard(null);
        setLoading(true);
        // trsrastreo.com.bo / YPFB Nacional / YPFB Internacional: reusa la búsqueda
        // global de vehículos (ya normaliza los 3 formatos distintos que devuelve cada
        // plataforma), en vez de parsear cada respuesta cruda acá. Grupo y usuarios de
        // TRS Rastreo se traen aparte: el listado global no trae esa relación. El
        // vehiculo puntual (listarVehiculosTrsRastreo con `buscar`) ya trae
        // usuarios_relacionados resuelto del lado del servidor (directos + por
        // cuadrilla, igual que "Usuarios relacionados" en TrsRastreoVehiculosPage) —
        // más completo que armarlo a mano desde los grupos (que solo cubren vinculos
        // via cuadrilla, no los directos usuario-vehiculo). En TRS Rastreo el campo
        // "Modem" de cada vehículo a veces se carga con el EMAI y no con el num_sim
        // (visto en producción) — por eso se busca por ambos valores y se mergean los
        // resultados, igual que ya se hace para `buscarVehiculoGlobal`.
        const [f1, f2, wi, le, gl, gtrs, ...vtrsResults] = await Promise.allSettled([
          searchFlespiPrimary(searchValues),
          searchFlespiSecondary(searchValues),
          searchWialonUnits(searchValues),
          searchLeasecontrolUnits(searchValues),
          buscarVehiculoGlobal(searchValues[0], modem?.emai),
          listarGruposTrsRastreo(),
          ...searchValues.map((valor) => listarVehiculosTrsRastreo({ buscar: valor, pageSize: 5 })),
        ]);
        if (!active) return;
        const globales = gl.status === "fulfilled" ? gl.value?.data || [] : [];
        const porFuente = (fuente) => globales.find((v) => v.fuente === fuente) || null;
        const rastreoBo = porFuente("rastreo_bo");
        setPlatforms({
          flespi1: f1.status === "fulfilled" ? f1.value.data?.[0] : null,
          flespi2: f2.status === "fulfilled" ? f2.value.data?.[0] : null,
          wialon: wi.status === "fulfilled" ? wi.value.data?.data?.[0] : null,
          leasecontrol: le.status === "fulfilled" ? le.value.data?.data?.[0] : null,
          internacional: porFuente("YPFB internacional"),
          nacional: porFuente("YPFB nacional"),
          rastreoBo,
        });
        setGruposTrs(gtrs.status === "fulfilled" ? gtrs.value?.data?.data || [] : []);
        const vehiculosTrs = [];
        const idsVehiculosTrs = new Set();
        vtrsResults.forEach((res) => {
          const items = res.status === "fulfilled" ? res.value?.data?.data || [] : [];
          items.forEach((v) => {
            if (!idsVehiculosTrs.has(v.id)) {
              vehiculosTrs.push(v);
              idsVehiculosTrs.add(v.id);
            }
          });
        });
        setVehiculoTrs(
          (rastreoBo?.uid && vehiculosTrs.find((v) => String(v.id) === String(rastreoBo.uid)))
          || vehiculosTrs[0]
          || null
        );
      } catch (e) {
        if (active) onError?.(e?.response?.data?.message || e?.message || "No se pudieron consultar plataformas");
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [searchKey, modem?.num_sim, modem?.emai, onError]);

  // Grupos activos donde el vehiculo de TRS Rastreo (por su uid) esta
  // vinculado — mismo criterio que TrsRastreoDetalle (ClientePlataformaPanel.jsx),
  // pero yendo del vehiculo hacia sus grupos en vez de al reves.
  const gruposDelVehiculoTrs = useMemo(() => {
    const uid = platforms.rastreoBo?.uid;
    if (!uid) return [];
    return gruposTrs.filter((g) =>
      (g.cuadrilla_relacion || []).some((r) => r.cuarel_activo && String(r.for_id) === String(uid))
    );
  }, [gruposTrs, platforms.rastreoBo]);

  const nombresGruposTrs = gruposDelVehiculoTrs.map((g) => g.cua_descripcion).filter(Boolean).join(", ") || "-";
  // usuarios_relacionados de vehiculoTrs ya viene resuelto del lado del servidor
  // (directos + por cuadrilla) — a diferencia de derivarlo solo de gruposTrs, que
  // se queda corto con los vinculos usuario-vehiculo directos (sin grupo).
  const nombresUsuariosTrs = (vehiculoTrs?.usuarios_relacionados || [])
    .filter((r) => r.estado_relacion)
    .map((r) => r.usuario?.usu_username || r.usuario?.usu_nombre)
    .filter(Boolean)
    .join(", ") || "-";

  const cards = [
    {
      title: "Flespi 1",
      data: platforms.flespi1,
      diagnostico: platforms.flespi1?.id && { clave: "flespi1", nombre: "Flespi 1", tipo: "flespi", id: platforms.flespi1.id, secundaria: false },
      keyName: "name",
      color: "#1976d2",
      bg: "#f0f7ff",
      rows: [
        { label: "Ident", value: platforms.flespi1?.configuration?.ident },
        { label: "Subcuenta", value: platforms.flespi1?.subcuenta },
        { label: "Ignicion", value: platforms.flespi1?.telemetry?.["engine.ignition.status"] === true ? "ON" : platforms.flespi1?.telemetry?.["engine.ignition.status"] === false ? "OFF" : "-" },
        { label: "Velocidad", value: platforms.flespi1 ? `${platforms.flespi1.telemetry?.position?.speed ?? platforms.flespi1.telemetry?.["position.speed"] ?? 0} km/h` : "" },
        { label: "Ultimo", value: formatUnixDateTime(platforms.flespi1?.telemetry?.timestamp) },
      ],
    },
    {
      title: "Flespi 2",
      data: platforms.flespi2,
      diagnostico: platforms.flespi2?.id && { clave: "flespi2", nombre: "Flespi 2", tipo: "flespi", id: platforms.flespi2.id, secundaria: true },
      keyName: "name",
      color: "#0288d1",
      bg: "#f0f9ff",
      rows: [
        { label: "Ident", value: platforms.flespi2?.configuration?.ident },
        { label: "Subcuenta", value: platforms.flespi2?.subcuenta },
        { label: "Ignicion", value: platforms.flespi2?.telemetry?.["engine.ignition.status"] === true ? "ON" : platforms.flespi2?.telemetry?.["engine.ignition.status"] === false ? "OFF" : "-" },
        { label: "Bateria", value: platforms.flespi2 ? `${platforms.flespi2.telemetry?.["battery.level"] ?? "-"}%` : "" },
        { label: "Ultimo", value: formatUnixDateTime(platforms.flespi2?.telemetry?.["server.timestamp"]) },
      ],
    },
    {
      title: "Wialon",
      data: platforms.wialon,
      diagnostico: platforms.wialon && { clave: "wialon", nombre: "Wialon", tipo: "wialon", id: platforms.wialon.id, ts: platforms.wialon.lmsg?.t || null, params: platforms.wialon.lmsg?.p },
      keyName: "nm",
      color: "#ed6c02",
      bg: "#fff7f0",
      rows: [
        { label: "UID", value: platforms.wialon?.uid },
        { label: "GSM", value: platforms.wialon?.lmsg?.p?.gsm },
        { label: "Voltaje", value: platforms.wialon ? `${platforms.wialon.lmsg?.p?.voltage ?? "-"} V` : "" },
        { label: "Ultimo", value: formatUnixDateTime(platforms.wialon?.lmsg?.t) },
        { label: "Grupo", value: formatGrupos(platforms.wialon?.grupos) },
        { label: "Usuario", value: formatUsuarios(platforms.wialon?.usuarios) },
      ],
    },
    {
      title: "LeaseControl",
      data: platforms.leasecontrol,
      diagnostico: platforms.leasecontrol && { clave: "leasecontrol", nombre: "LeaseControl", tipo: "leasecontrol", id: platforms.leasecontrol.id, ts: platforms.leasecontrol.lmsg?.t || null, params: platforms.leasecontrol.lmsg?.p },
      keyName: "nm",
      color: "#9c27b0",
      bg: "#fbf0ff",
      rows: [
        { label: "UID", value: platforms.leasecontrol?.uid },
        { label: "Comando", value: platforms.leasecontrol?.lmsg?.p?.command },
        { label: "GPS valido", value: gpsValidoTexto(platforms.leasecontrol?.lmsg?.p?.gps_valid) },
        { label: "Ultimo", value: formatUnixDateTime(platforms.leasecontrol?.lmsg?.t) },
        { label: "Grupo", value: formatGrupos(platforms.leasecontrol?.grupos) },
        { label: "Usuario", value: formatUsuarios(platforms.leasecontrol?.usuarios) },
      ],
    },
    {
      title: "TRS Rastreo",
      data: platforms.rastreoBo,
      keyName: "placa",
      color: "#2e7d32",
      bg: "#f1f8f1",
      rows: [
        { label: "Alias", value: platforms.rastreoBo?.alias },
        { label: "UID", value: platforms.rastreoBo?.uid },
        { label: "Módem", value: platforms.rastreoBo?.modem },
        { label: "Ultimo", value: platforms.rastreoBo?.fecha_ultimo_registro },
        { label: "Grupo", value: nombresGruposTrs },
        { label: "Usuario", value: nombresUsuariosTrs },
      ],
    },
    {
      title: "YPFB Nacional",
      data: platforms.nacional,
      keyName: "placa",
      color: "#c62828",
      bg: "#fdf1f1",
      rows: ypfbRows(platforms.nacional),
    },
    {
      title: "YPFB Internacional",
      data: platforms.internacional,
      keyName: "placa",
      color: "#ad1457",
      bg: "#fdf0f5",
      rows: ypfbRows(platforms.internacional),
    },
  ];

  // Último dato de cada plataforma donde está el equipo: el diagnóstico de una tarjeta se compara con las demás para
  // detectar el caso de un equipo que se pasó a otra plataforma y quedó la unidad anterior sin desactivar.
  const ultimosPorPlataforma = useMemo(() => [
    platforms.flespi1 && { clave: "flespi1", nombre: "Flespi 1", ts: first(platforms.flespi1.telemetry?.["server.timestamp"], platforms.flespi1.telemetry?.timestamp) || null },
    platforms.flespi2 && { clave: "flespi2", nombre: "Flespi 2", ts: first(platforms.flespi2.telemetry?.["server.timestamp"], platforms.flespi2.telemetry?.timestamp) || null },
    platforms.wialon && { clave: "wialon", nombre: "Wialon", ts: platforms.wialon.lmsg?.t || null },
    platforms.leasecontrol && { clave: "leasecontrol", nombre: "LeaseControl", ts: platforms.leasecontrol.lmsg?.t || null },
    platforms.rastreoBo && { clave: "rastreoBo", nombre: "TRS Rastreo", ts: fechaLaPazAUnix(platforms.rastreoBo.fecha_ultimo_registro) },
    platforms.nacional && { clave: "nacional", nombre: "YPFB Nacional", ts: fechaLaPazAUnix(platforms.nacional.fecha_ultimo_registro) },
    platforms.internacional && { clave: "internacional", nombre: "YPFB Internacional", ts: fechaLaPazAUnix(platforms.internacional.fecha_ultimo_registro) },
  ].filter(Boolean)
    // TRS Rastreo / YPFB: si la fecha no se pudo leer se dejan fuera, para no mostrarlas como "sin datos".
    .filter((o) => o.ts || !["rastreoBo", "nacional", "internacional"].includes(o.clave)), [platforms]);

  return (
    <Paper variant="outlined" sx={{ borderRadius: 1, overflow: "hidden" }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 0.8, py: 0.45, bgcolor: tableHeadBg, borderBottom: "1px solid", borderColor: "divider" }}>
        <Typography sx={{ fontSize: 11.8, fontWeight: 900, color: "text.primary" }}>
          Plataformas donde esta reportando
        </Typography>
        {loading && <CircularProgress size={13} thickness={5} />}
      </Stack>
      {loading ? (
        <Box sx={{ p: 1.2 }}>
          <Typography sx={{ fontSize: 11.5, color: "text.secondary", fontStyle: "italic" }}>
            Consultando plataformas...
          </Typography>
        </Box>
      ) : cards.some((card) => card.data) ? (
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 0.8, p: 0.8 }}>
          {cards.filter((card) => card.data).map((card) => (
            <PlatformCard
              key={card.title}
              {...card}
              ts={ultimosPorPlataforma.find((u) => u.nombre === card.title)?.ts ?? null}
              onOpen={() => setDetailCard(card)}
              onDiagnosticar={card.diagnostico ? () => setDiagnostico(card.diagnostico) : undefined}
            />
          ))}
        </Box>
      ) : (
        <Box sx={{ p: 1.2 }}>
          <Typography sx={{ fontSize: 11.5, color: "text.secondary", fontStyle: "italic" }}>
            No se encontró en ninguna plataforma.
          </Typography>
        </Box>
      )}

      <DiagnosticoReporteDialog plataforma={diagnostico} todas={ultimosPorPlataforma} onClose={() => setDiagnostico(null)} />

      <PlatformDetailDialog
        card={detailCard}
        ts={detailCard ? ultimosPorPlataforma.find((u) => u.nombre === detailCard.title)?.ts ?? null : undefined}
        onClose={() => setDetailCard(null)}
        onDiagnosticar={detailCard?.diagnostico ? () => setDiagnostico(detailCard.diagnostico) : undefined}
      />
    </Paper>
  );
}

// Solo para chips de roaming internacional (modelo "Emnify Internacional"): consulta la
// API de Emnify por ICCID (campo "emai" en nuestra BD) y muestra estado del endpoint/SIM,
// tarifa y perfil de servicio. Silencioso si el chip no tiene emai o no existe en la
// cuenta de Emnify (la mayoria de los chips no son Emnify) — no mancha la pantalla con
// una tarjeta vacia para el caso comun.
function ChipEmnifyPanel({ chip = {}, onError }) {
  const [endpoint, setEndpoint] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const iccid = chip?.emai;

  useEffect(() => {
    let active = true;
    if (!iccid) {
      setEndpoint(null);
      return undefined;
    }
    setLoading(true);
    buscarEmnifyPorIccid(iccid)
      .then((res) => {
        if (active) setEndpoint(res.data?.data || null);
      })
      .catch((e) => {
        if (active) {
          setEndpoint(null);
          onError?.(e?.response?.data?.message || e?.message || "No se pudo consultar Emnify");
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [iccid, onError]);

  if (!iccid || (!loading && !endpoint)) return null;

  // location: ubicacion por celda (GCID/LBS), no GPS real — puede venir null si Emnify
  // nunca la calculo para este endpoint (deshabilitado, sin actividad reciente, etc.).
  const detailCoords = getPlatformCoords(endpoint?.location);
  // connectivity.location.last_updated viene sin marca de zona horaria pero en UTC
  // (confirmado en vivo: "13:55:49" ahi corresponde a "9:55" hora Bolivia, -4) — se le
  // agrega "Z" antes de formatear para que no se interprete como hora local del navegador.
  const conectividad = endpoint?.connectivity;
  const conectividadRegistro = conectividad?.location;
  const conectividadTexto = conectividad
    ? [conectividad.status?.description, conectividad.pdp_context?.rat_type?.description]
        .filter(Boolean)
        .join(" ")
    : undefined;
  const conectividadUltimaActRed = formatIsoDateTime(
    conectividadRegistro?.last_updated
      ? `${conectividadRegistro.last_updated.replace(" ", "T")}Z`
      : null
  );

  const card = {
    title: "Emnify",
    data: endpoint,
    keyName: "name",
    color: "#00897b",
    bg: "#eefaf7",
    rows: [
      { label: "Conexion", value: conectividadTexto },
      { label: "Ult. act. red", value: conectividadRegistro ? conectividadUltimaActRed : undefined },
      { label: "Estado endpoint", value: endpoint?.status?.description },
      { label: "Estado SIM", value: endpoint?.sim?.status?.description },
      { label: "MSISDN", value: endpoint?.sim?.msisdn },
      { label: "Tarifa", value: endpoint?.tariff_profile?.name },
      { label: "Ultima modif.", value: formatIsoDateTime(endpoint?.last_updated) },
    ],
  };

  return (
    <Paper variant="outlined" sx={{ borderRadius: 1, overflow: "hidden" }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 0.8, py: 0.45, bgcolor: tableHeadBg, borderBottom: "1px solid", borderColor: "divider" }}>
        <Typography sx={{ fontSize: 11.8, fontWeight: 900, color: "text.primary" }}>
          Plataforma Emnify
        </Typography>
        {loading && <CircularProgress size={13} thickness={5} />}
      </Stack>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 0.8, p: 0.8 }}>
        <PlatformCard {...card} onOpen={() => setDetailOpen(true)} />
      </Box>

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: 14, fontWeight: 900, py: 1 }}>Emnify</DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          {conectividad && (
            <Paper variant="outlined" sx={{ p: 1, mb: 1 }}>
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: conectividad.status?.description === "ONLINE" ? "success.main" : "text.disabled",
                  }}
                />
                <Typography sx={{ fontSize: 13, fontWeight: 900 }}>
                  {conectividadTexto || "-"}
                </Typography>
              </Stack>
              {conectividadRegistro?.operator && (
                <Typography sx={{ fontSize: 11.5, mt: 0.3 }}>
                  {isoToFlagEmoji(conectividadRegistro.operator.country?.iso_code)} {conectividadRegistro.operator.name}, {conectividadRegistro.operator.country?.name}
                </Typography>
              )}
              <Stack spacing={0.35} sx={{ mt: 0.5 }}>
                <PlatformValue
                  label="Ultima actualizacion de red"
                  value={conectividadUltimaActRed}
                />
                {conectividad.pdp_context?.duration && (
                  <PlatformValue label="Contexto PDP activo" value={formatDuracionHHMMSS(conectividad.pdp_context.duration)} />
                )}
              </Stack>
            </Paper>
          )}
          <Paper
            variant="outlined"
            sx={{
              p: 1,
              bgcolor: (theme) => (theme.palette.mode !== "dark" ? "#eefaf7" : alpha("#00897b", 0.14)),
            }}
          >
            <Typography sx={{ fontSize: 12, fontWeight: 900 }} noWrap title={fmt(endpoint?.name)}>
              {fmt(endpoint?.name) || "-"}
            </Typography>
            <Stack spacing={0.35} sx={{ mt: 0.5 }}>
              <PlatformValue label="Estado endpoint" value={endpoint?.status?.description} />
              <PlatformValue label="Estado SIM" value={endpoint?.sim?.status?.description} />
              <PlatformValue label="MSISDN" value={endpoint?.sim?.msisdn} />
              <PlatformValue label="IMSI" value={endpoint?.sim?.imsi} />
              <PlatformValue label="ICCID" value={endpoint?.sim?.iccid} />
              <PlatformValue label="IMEI" value={endpoint?.imei} />
              <PlatformValue label="Tarifa" value={endpoint?.tariff_profile?.name} />
              <PlatformValue label="Perfil de servicio" value={endpoint?.service_profile?.name} />
              <PlatformValue label="Última modificación" value={formatIsoDateTime(endpoint?.last_updated)} />
              {endpoint?.location?.accuracy != null && (
                <PlatformValue label="Precision" value={`~${Math.round(endpoint.location.accuracy)} m`} />
              )}
            </Stack>
          </Paper>
          {(endpoint?.stats?.last_month?.data || endpoint?.stats?.current_month?.data) && (
            <Paper variant="outlined" sx={{ overflow: "hidden", borderRadius: 1, mt: 1 }}>
              <Box sx={{ px: 1, py: 0.6, bgcolor: tableHeadBg, borderBottom: "1px solid", borderColor: "divider" }}>
                <Typography sx={{ fontSize: 12, fontWeight: 900 }}>
                  Consumo de datos
                </Typography>
              </Box>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, p: 1 }}>
                {[
                  { titulo: "Ultimo mes", d: endpoint?.stats?.last_month?.data },
                  { titulo: "Mes actual", d: endpoint?.stats?.current_month?.data },
                ].filter((b) => b.d).map((b) => (
                  <Box key={b.titulo} sx={{ flex: "1 1 160px", minWidth: 150 }}>
                    <Typography sx={{ fontSize: 10.5, fontWeight: 800, color: "text.secondary", mb: 0.2 }}>
                      {b.titulo} ({formatMesAnio(b.d.month)})
                    </Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 900, color: "#00897b" }}>
                      {formatMB(b.d.volume)}
                    </Typography>
                    <Stack direction="row" spacing={1.5} sx={{ mt: 0.2 }}>
                      <Typography sx={{ fontSize: 10.5, color: "text.secondary" }}>↓ {formatMB(b.d.volume_rx)}</Typography>
                      <Typography sx={{ fontSize: 10.5, color: "text.secondary" }}>↑ {formatMB(b.d.volume_tx)}</Typography>
                    </Stack>
                  </Box>
                ))}
              </Box>
            </Paper>
          )}
          {detailCoords && (
            <Paper variant="outlined" sx={{ overflow: "hidden", borderRadius: 1, mt: 1 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 1, py: 0.6, bgcolor: tableHeadBg, borderBottom: "1px solid", borderColor: "divider" }}>
                <Typography sx={{ fontSize: 12, fontWeight: 900 }}>
                  Ubicacion (por celda, no GPS)
                </Typography>
                <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                  {detailCoords.latitude.toFixed(6)}, {detailCoords.longitude.toFixed(6)}
                </Typography>
              </Stack>
              <Box
                component="iframe"
                title="Mapa Emnify"
                src={`https://www.openstreetmap.org/export/embed.html?bbox=${detailCoords.longitude - 0.01}%2C${detailCoords.latitude - 0.01}%2C${detailCoords.longitude + 0.01}%2C${detailCoords.latitude + 0.01}&layer=mapnik&marker=${detailCoords.latitude}%2C${detailCoords.longitude}`}
                sx={{ width: "100%", height: 260, border: 0, display: "block" }}
              />
              <Box sx={{ px: 1, py: 0.5 }}>
                <a
                  href={`https://www.google.com/maps?q=${detailCoords.latitude},${detailCoords.longitude}`}
                  target="_blank"
                  rel="noreferrer"
                  style={{ fontSize: 11, fontWeight: 700, color: "inherit" }}
                >
                  Abrir mapa
                </a>
              </Box>
            </Paper>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5 }}>
          <Button size="small" onClick={() => setDetailOpen(false)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

const HISTORIAL_TITULOS = {
  modem: "Historial modem",
  placa: "Historial Placa",
  chip: "Historial Chip",
  radio: "Historial Radio",
  bateria: "Historial Bateria",
  varios: "Historial Equipo",
};

const toAbsUrl = (url) => {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  const base = ENV.API_BASE_URL || "";
  if (!base) return url;
  return `${base.replace(/\/$/, "")}/${String(url).replace(/^\//, "")}`;
};

const HISTORIAL_COLUMN_LABELS = {
  cod_req: "Cod ot/Req",
  cod_ot: "Cod ot/Req",
  num_sim: "Modem",
  id: "Id",
  asignado: "Asignado a",
  propiedad: "Propiedad",
  cod_servicio: "Cliente servicio",
  grupo: "Grupo",
  placa: "Placa",
  ciudad: "Ciudad",
  estado: "Estado",
  operacion: "Operacion",
  fecha: "Fecha de registro",
  fecha_registro: "Fecha de registro",
  observacion: "Observacion",
  usu: "Usuario",
  usuario: "Usuario",
};

function HistorialTable({ rows = [], tipo = "modem" }) {
  const esPlaca = tipo === "placa";
  const [imagenPreview, setImagenPreview] = useState(null);

  const columns = useMemo(() => {
    const keys = new Set();

    rows.slice(0, 10).forEach((row) => {
      Object.keys(row || {}).forEach((key) => keys.add(key));
    });
    keys.delete("imagen_url");

    const preferred = esPlaca
      ? [
        "cod_req",
        "cod_ot",
        "num_sim",
        "id",
        "propiedad",
        "asignado",
        "cod_servicio",
        "grupo",
        "placa",
        "ciudad",
        "estado",
        "operacion",
        "fecha",
        "fecha_registro",
        "observacion",
        "usu",
        "usuario",
        "id_historial",
        "numero",
        "serie",
        "id_ref",
        "sociedad",
        "transportista",
      ]
      : [
        "cod_req",
        "cod_ot",
        "propiedad",
        "asignado",
        "cod_servicio",
        "grupo",
        "placa",
        "ciudad",
        "estado",
        "operacion",
        "fecha",
        "fecha_registro",
        "observacion",
        "usu",
        "usuario",
        "id_historial",
        "num_sim",
        "numero",
        "serie",
        "id_ref",
        "sociedad",
        "transportista",
      ];

    const hidden = ["propiedad_nombre", "asignado_nombre", "cod_servicio_nombre"];

    return [
      ...preferred.filter((key) => keys.has(key)),
      ...[...keys]
        .filter((key) => !preferred.includes(key) && !hidden.includes(key))
        .slice(0, 10),
    ];
  }, [rows, esPlaca]);

  if (!rows.length) {
    return (
      <Paper variant="outlined" sx={{ p: 1, borderRadius: 1 }}>
        <Typography
          sx={{
            fontSize: 12,
            color: "text.secondary",
            fontStyle: "italic",
          }}
        >
          Sin historial registrado.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 1,
        overflow: "hidden",
      }}
    >
      <Typography
        align="center"
        sx={{
          py: "3px",
          fontSize: 11.5,
          fontWeight: 800,
          bgcolor: tableHeadBg,
          color: "text.secondary",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        {HISTORIAL_TITULOS[tipo] || "Historial modem"}
      </Typography>

      <TableContainer sx={{ maxHeight: "calc(100vh - 385px)" }}>
        <Table
          size="small"
          stickyHeader
          sx={{
            "& .MuiTableCell-root": {
              py: "2.5px",
              px: "6px",
              fontSize: 11.5,
              whiteSpace: "nowrap",
            },
          }}
        >
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column}
                  sx={{
                    fontWeight: 800,
                    bgcolor: (theme) => `${tableHeadBg(theme)} !important`,
                    fontSize: 11,
                  }}
                >
                  {esPlaca
                    ? HISTORIAL_COLUMN_LABELS[column] || column
                    : column === "cod_servicio" ? "cliente servicio" : column}
                </TableCell>
              ))}
              <TableCell
                align="center"
                sx={{
                  fontWeight: 800,
                  bgcolor: (theme) => `${tableHeadBg(theme)} !important`,
                  fontSize: 11,
                }}
              >
                Imagen
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.map((row, index) => (
              <TableRow
                key={row.id_historial || row.id || index}
                hover
                sx={{
                  "&:nth-of-type(even)": {
                    bgcolor: (theme) => theme.palette.action.hover,
                  },
                }}
              >
                {columns.map((column) => {
                  let value = row[column];
                  if (column === "propiedad") value = row.propiedad_nombre || row.propiedad;
                  else if (column === "asignado") value = row.asignado_nombre || row.asignado;
                  else if (column === "cod_servicio") value = row.cod_servicio_nombre || row.cod_servicio;
                  return <TableCell key={column}>{fmt(value) || "—"}</TableCell>;
                })}
                <TableCell align="center">
                  {row.imagen_url ? (
                    <Box
                      role="button"
                      tabIndex={0}
                      onClick={() => setImagenPreview(row.imagen_url)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") setImagenPreview(row.imagen_url);
                      }}
                      sx={{ display: "inline-flex", cursor: "pointer", color: "primary.main" }}
                    >
                      <ImageOutlinedIcon sx={{ fontSize: 17 }} />
                    </Box>
                  ) : (
                    <HideImageOutlinedIcon sx={{ fontSize: 17, color: "text.disabled" }} />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={!!imagenPreview} onClose={() => setImagenPreview(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ py: 1, fontSize: "0.95rem", fontWeight: 900 }}>Imagen / Evidencia</DialogTitle>
        <DialogContent dividers sx={{ p: 1, textAlign: "center" }}>
          {imagenPreview && (
            <Box
              component="img"
              src={toAbsUrl(imagenPreview)}
              alt="Evidencia"
              sx={{
                display: "block",
                width: "100%",
                maxHeight: "70vh",
                objectFit: "contain",
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "#fff",
                mx: "auto",
              }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ p: 1 }}>
          <Button size="small" href={toAbsUrl(imagenPreview)} target="_blank" rel="noreferrer">
            Abrir en pestaña nueva
          </Button>
          <Button size="small" variant="contained" onClick={() => setImagenPreview(null)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

// ─────────────────────────────────────────────
// Builders de campos
// ─────────────────────────────────────────────

function buildEquipoFields(equipo = {}, tipo = "") {
  if (tipo === "varios") {
    return {
      left: [
        {
          label: "Serie:",
          value: equipo.serie,
          link: equipo.serie ? "#" : "",
        },
        {
          label: "EMAI:",
          value: equipo.emai,
        },
        {
          label: "Modelo:",
          value: equipo.modelo,
        },
        {
          label: "Tipo:",
          value: equipo.tipo,
        },
        {
          label: "Relacion:",
          value: equipo.relacion,
        },
        {
          label: "Observacion:",
          value: equipo.observacion,
        },
      ],
      right: [
        {
          label: "Estado:",
          value: equipo.estado,
        },
        {
          label: "Ciudad:",
          value: equipo.ciudad,
        },
        {
          label: "Asignado:",
          value: equipo.asignado,
        },
        {
          label: "Propiedad:",
          value: equipo.propiedad,
        },
        {
          label: "Cliente servicio:",
          value: equipo.cod_servicio,
        },
        {
          label: "F. registro:",
          value: first(equipo.fecha_registro, equipo.fecha),
        },
      ],
    };
  }

  if (tipo === "bateria") {
    return {
      left: [
        {
          label: "Serie:",
          value: equipo.serie,
          link: equipo.serie ? "#" : "",
        },
        {
          label: "ID:",
          value: first(equipo.ID, equipo.id),
        },
        {
          label: "Modelo:",
          value: equipo.modelo,
        },
        {
          label: "Marca:",
          value: equipo.marca,
        },
        {
          label: "Relacion:",
          value: equipo.relacion,
        },
        {
          label: "Observacion:",
          value: equipo.observacion,
        },
      ],
      right: [
        {
          label: "Estado:",
          value: equipo.estado,
        },
        {
          label: "Ciudad:",
          value: equipo.ciudad,
        },
        {
          label: "Propiedad:",
          value: equipo.propiedad,
        },
        {
          label: "Asignado:",
          value: equipo.asignado,
        },
        {
          label: "Cliente servicio:",
          value: equipo.cod_servicio,
        },
        {
          label: "F. registro:",
          value: first(equipo.fecha_registro, equipo.fecha),
        },
      ],
    };
  }

  if (tipo === "chip") {
    return {
      left: [
        {
          label: "Numero:",
          value: equipo.numero,
          link: equipo.numero ? "#" : "",
        },
        {
          label: "EMAI:",
          value: equipo.emai,
        },
        {
          label: "Modelo:",
          value: equipo.modelo,
        },
        {
          label: "Num:",
          value: equipo.num,
        },
        {
          label: "Relacion:",
          value: equipo.relacion,
        },
        {
          label: "Observacion:",
          value: equipo.observacion,
        },
      ],
      right: [
        {
          label: "Estado:",
          value: equipo.estado,
        },
        {
          label: "Ciudad:",
          value: equipo.ciudad,
        },
        {
          label: "Asignado:",
          value: equipo.asignado,
        },
        {
          label: "Cliente servicio:",
          value: equipo.cod_servicio,
        },
        {
          label: "F. registro:",
          value: first(equipo.fecha_registro, equipo.fecha),
        },
      ],
    };
  }

  if (tipo === "radio") {
    return {
      left: [
        {
          label: "Serie:",
          value: equipo.serie,
          link: equipo.serie ? "#" : "",
        },
        {
          label: "ID:",
          value: first(equipo.ID, equipo.id),
        },
        {
          label: "Modelo:",
          value: equipo.modelo,
        },
        {
          label: "Marca:",
          value: equipo.marca,
        },
        {
          label: "Alias:",
          value: first(equipo.id_ref, equipo.alias),
        },
        {
          label: "Observacion:",
          value: equipo.observacion,
        },
      ],
      right: [
        {
          label: "Estado:",
          value: equipo.estado,
        },
        {
          label: "Placa:",
          value: equipo.placa,
        },
        {
          label: "Ciudad:",
          value: equipo.ciudad,
        },
        {
          label: "Propiedad:",
          value: equipo.propiedad,
        },
        {
          label: "Grupo:",
          value: equipo.grupo,
        },
        {
          label: "F. registro:",
          value: first(equipo.fecha_registro, equipo.fecha),
        },
      ],
    };
  }

  if (tipo === "placa") {
    return {
      left: [
        {
          label: "Placa:",
          value: equipo.placa,
          link: equipo.placa ? "#" : "",
        },
        {
          label: "Marca:",
          value: equipo.marca,
        },
        {
          label: "Modelo:",
          value: equipo.modelo,
        },
      ],
      right: [
        {
          label: "Estado:",
          value: equipo.estado,
        },
        {
          label: "Tipo vehiculo:",
          value: equipo.tipo_vehiculo,
        },
        {
          label: "Color:",
          value: equipo.color,
        },
      ],
    };
  }

  return {
    left: [
      {
        label: "Num sim:",
        value: equipo.num_sim,
        link: equipo.num_sim ? "#" : "",
      },
      {
        label: "Id:",
        value: first(equipo.id, equipo.ID_A),
      },
      {
        label: "EMAI:",
        value: equipo.emai,
      },
      {
        label: "Modelo:",
        value: equipo.modelo,
      },
      {
        label: "Marca:",
        value: equipo.marca,
      },
      {
        label: "Alias:",
        value: first(equipo.id_ref, equipo.alias),
      },
      {
        label: "Orden compra:",
        value: first(equipo.orden_compra, equipo.orden, equipo.oc),
      },
      {
        label: "Observación:",
        value: equipo.observacion,
      },
    ],
    right: [
      {
        label: "Estado:",
        value: equipo.estado,
      },
      {
        label: "Placa:",
        value: equipo.placa,
      },
      {
        label: "Ciudad:",
        value: equipo.ciudad,
      },
      {
        label: "Propiedad:",
        value: equipo.propiedad,
      },
      {
        label: "Bandera:",
        value: equipo.bandera,
      },
      {
        label: "Grupo:",
        value: equipo.grupo,
      },
      {
        label: "F. registro:",
        value: first(equipo.fecha_registro, equipo.fecha),
      },
      {
        label: "Transportista:",
        value: equipo.transportista,
      },
    ],
  };
}

function buildChipFields(chips = []) {
  const chip = chips[0] || {};

  return [
    {
      label: "Número:",
      value: first(chip.numero, chip.num),
      link: chip.numero ? "#" : "",
    },
    {
      label: "ICC:",
      value: first(chip.icc, chip.emai, chip.relacion),
    },
    {
      label: "Modelo:",
      value: chip.modelo,
    },
    {
      label: "Estado:",
      value: chip.estado,
    },
  ];
}

function buildBateriaFields(baterias = []) {
  const bateria = baterias[0] || {};

  return [
    {
      label: "Serie:",
      value: bateria.serie,
      link: bateria.serie ? "#" : "",
    },
    {
      label: "ID:",
      value: first(bateria.ID, bateria.id),
    },
    {
      label: "Modelo:",
      value: bateria.modelo,
    },
    {
      label: "Marca:",
      value: bateria.marca,
    },
    {
      label: "Estado:",
      value: bateria.estado,
    },
    {
      label: "Ciudad:",
      value: bateria.ciudad,
    },
  ];
}

function buildRadioFields(radios = []) {
  const radio = radios[0] || {};

  return [
    {
      label: "Serie:",
      value: radio.serie,
      link: radio.serie ? "#" : "",
    },
    {
      label: "ID:",
      value: first(radio.ID, radio.id),
    },
    {
      label: "Modelo:",
      value: radio.modelo,
    },
    {
      label: "Marca:",
      value: radio.marca,
    },
    {
      label: "Estado:",
      value: radio.estado,
    },
    {
      label: "Ciudad:",
      value: radio.ciudad,
    },
    {
      label: "Placa:",
      value: radio.placa,
    },
  ];
}

function buildModemFields(modems = []) {
  const modem = modems[0] || {};

  return [
    {
      label: "Num sim:",
      value: modem.num_sim,
      link: modem.num_sim ? "#" : "",
    },
    {
      label: "ID:",
      value: first(modem.id, modem.ID_A),
    },
    {
      label: "EMAI:",
      value: modem.emai,
    },
    {
      label: "Modelo:",
      value: modem.modelo,
    },
    {
      label: "Estado:",
      value: modem.estado,
    },
    {
      label: "Ciudad:",
      value: modem.ciudad,
    },
    {
      label: "Placa:",
      value: modem.placa,
    },
  ];
}

function buildVariosRelacionFields(relacionados = [], chips = []) {
  const relacionado = relacionados[0] || {};
  const chip = chips[0] || {};

  return [
    {
      label: "Relacionado:",
      value: relacionado.serie,
      link: relacionado.serie ? "#" : "",
    },
    {
      label: "Modelo rel.:",
      value: relacionado.modelo,
    },
    {
      label: "Estado rel.:",
      value: relacionado.estado,
    },
    {
      label: "Ciudad rel.:",
      value: relacionado.ciudad,
    },
    {
      label: "Chip:",
      value: first(chip.numero, chip.num),
      link: chip.numero ? "#" : "",
    },
    {
      label: "Chip modelo:",
      value: chip.modelo,
    },
    {
      label: "Chip estado:",
      value: chip.estado,
    },
  ];
}

function buildClienteFields(clientes = []) {
  if (!clientes.length) return [];

  return clientes.flatMap((cliente, index) => [
    {
      label: `Cliente ${index + 1}:`,
      value: first(cliente.razon_social, cliente.cliente, cliente.cod_cliente),
    },
    {
      label: "Servicio:",
      value: [
        cliente.cod_servicio_cliente,
        cliente.descripcion,
        cliente.tipo_vinculacion,
        cliente.estado,
        cliente.estado_relacion,
      ].filter(Boolean).join(" - "),
    },
    {
      label: "Sucursal:",
      value: cliente.sucursal_facturar,
    },
  ]);
}

// ─────────────────────────────────────────────
// Componente principal
// ─────────────────────────────────────────────

export default function BusquedaPorIdentificador({
  initialTipo = "modem",
  initialBuscar = "",
  autoLoadKey = "",
  hideSearch = false,
}) {
  const { user } = useAuth();
  const role = cleanSearchValue(user?.role).toLowerCase();
  const subRol = cleanSearchValue(user?.sub_rol).toLowerCase();
  const canManageInventory = subRol === "inventario" || role === "admin" || role === "gerente";
  const [buscar, setBuscar] = useState(initialBuscar);
  const [tipo, setTipo] = useState(initialTipo);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [detalleLoading, setDetalleLoading] = useState(false);
  const [detalleError, setDetalleError] = useState("");
  // Solo aplica a tipo="modem": 0 = historial del propio módem, 1 = historial
  // de los chips que estuvieron relacionados a él (ver historialChips en
  // getDetalleModem). Se resetea cada vez que llega un detalle nuevo, para no
  // quedar en la pestaña de chips al buscar otro equipo.
  const [historialTab, setHistorialTab] = useState(0);
  const [obsOpen, setObsOpen] = useState(false);
  const [obsValue, setObsValue] = useState("");
  const [obsSaving, setObsSaving] = useState(false);
  const [dataDialog, setDataDialog] = useState(null);
  const [dataValue, setDataValue] = useState("");
  const [dataSaving, setDataSaving] = useState(false);
  const [dataDialogError, setDataDialogError] = useState("");
  const [placaConflicto, setPlacaConflicto] = useState(null);
  const [placaReasignando, setPlacaReasignando] = useState(false);
  const [estadoDialogOpen, setEstadoDialogOpen] = useState(false);
  const [modeloDialogOpen, setModeloDialogOpen] = useState(false);
  const [estadoValue, setEstadoValue] = useState("");
  const [estadoObs, setEstadoObs] = useState("");
  const [estadoSaving, setEstadoSaving] = useState(false);
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });
  const [modemModalSerie, setModemModalSerie] = useState(null);
  const [chipModalNumero, setChipModalNumero] = useState(null);
  const [recientes, setRecientes] = useState(leerRecientes);

  const loadDetalle = useCallback(async (row) => {
    const id = normalizeId(row);

    if (!row?.tipo || !id) return;

    try {
      setSelected(row);
      setDetalleLoading(true);
      setDetalleError("");
      setHistorialTab(0);

      const { data } = await getCentroEquipoDetalle(row.tipo, id);
      setDetalle(data || null);
    } catch (e) {
      setDetalle(null);
      setDetalleError(
        e?.response?.data?.message ||
          e?.message ||
          "No se pudo cargar el detalle"
      );
    } finally {
      setDetalleLoading(false);
    }
  }, []);

  useEffect(() => {
    const value = String(initialBuscar || "").trim();
    if (!autoLoadKey || !value || !initialTipo) return;

    const row = {
      tipo: initialTipo,
      id: value,
      identificador: value,
    };

    setBuscar(value);
    setTipo(initialTipo);
    setRows([row]);
    loadDetalle(row);
  }, [autoLoadKey, initialBuscar, initialTipo, loadDetalle]);

  // `override` ({ buscar, tipo }) permite repetir una búsqueda guardada sin esperar a que el formulario se actualice.
  const doSearch = useCallback(async (override) => {
    const value = String(override?.buscar ?? buscar).trim();
    const tipoBusqueda = override?.tipo ?? tipo;

    if (!value) {
      setRows([]);
      setSelected(null);
      setDetalle(null);
      setError("Debe ingresar un valor para buscar.");
      return;
    }

    try {
      setLoading(true);
      setError("");
      setDetalleError("");

      const { data } = await buscarCentroEquipos({
        buscar: value,
        tipo: tipoBusqueda,
        limit: 10,
      });

      const result = Array.isArray(data) ? data : [];
      setRows(result);
      if (result.length) {
        const nueva = { buscar: value, tipo: tipoBusqueda };
        setRecientes((prev) => {
          const lista = [nueva, ...prev.filter((r) => !mismoReciente(r, nueva))].slice(0, MAX_RECIENTES);
          guardarRecientes(lista);
          return lista;
        });
      }

      const exact = result.find((row) => rowMatchesExactSearch(row, value));
      if (exact) {
        await loadDetalle(exact);
      } else {
        setSelected(null);
        setDetalle(null);
      }
    } catch (e) {
      setRows([]);
      setSelected(null);
      setDetalle(null);
      setError(
        e?.response?.data?.message || e?.message || "No se pudo buscar equipos"
      );
    } finally {
      setLoading(false);
    }
  }, [buscar, tipo, loadDetalle]);

  const repetirReciente = (r) => {
    setBuscar(r.buscar);
    setTipo(r.tipo);
    doSearch(r);
  };
  const quitarReciente = (r) => setRecientes((prev) => { const lista = prev.filter((x) => !mismoReciente(x, r)); guardarRecientes(lista); return lista; });
  const limpiarRecientes = () => { guardarRecientes([]); setRecientes([]); };
  const limpiarBusqueda = () => { setBuscar(""); setError(""); };

  const equipo = detalle?.equipo || selected || {};
  const chips = detalle?.relaciones?.chips || equipo.chips_detalle || [];
  const baterias = detalle?.relaciones?.baterias || [];
  const radios = detalle?.relaciones?.radios || [];
  const modems = detalle?.relaciones?.modems || [];
  const relacionados = detalle?.relaciones?.relacionados || [];
  const clientes = detalle?.relaciones?.clientes || [];
  const detalleTipo = detalle?.tipo || selected?.tipo || tipo;
  const equipoFields = buildEquipoFields(equipo, detalleTipo);
  const editableId = getEditableId(equipo, detalleTipo);
  const relationTitle = detalleTipo === "radio"
    ? "Bateria relacionada"
    : detalleTipo === "bateria"
      ? "Radio relacionada"
      : detalleTipo === "chip"
        ? "Modem relacionado"
        : detalleTipo === "varios"
          ? "Relaciones"
          : detalleTipo === "placa"
            ? "Modems relacionados"
            : "Chip relacionado";
  const relationFields = detalleTipo === "radio"
    ? buildBateriaFields(baterias)
    : detalleTipo === "bateria"
      ? buildRadioFields(radios)
      : detalleTipo === "chip"
        ? buildModemFields(modems)
        : detalleTipo === "varios"
          ? buildVariosRelacionFields(relacionados, chips)
          : buildChipFields(chips);

  const copyText = async (text) => {
    const value = String(text || "").trim();
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
      setDetalleError("");
      setToast({ open: true, message: "Identificador copiado", severity: "success" });
    } catch (e) {
      setToast({ open: true, message: "No se pudo copiar el identificador", severity: "error" });
    }
  };

  const openObsDialog = () => {
    setObsValue(equipo?.observacion || "");
    setObsOpen(true);
  };

  const saveObservacion = async () => {
    if (!editableId || !detalleTipo) return;

    try {
      setObsSaving(true);
      setDetalleError("");
      await actualizarObservacionEquipoCentro(detalleTipo, editableId, obsValue);
      setObsOpen(false);
      await loadDetalle({ tipo: detalleTipo, id: editableId, identificador: editableId });
    } catch (e) {
      setDetalleError(
        e?.response?.data?.message ||
          e?.message ||
          "No se pudo actualizar la observacion"
      );
    } finally {
      setObsSaving(false);
    }
  };

  const esTransicionActual = ["transicion", "transsicion", "trancicion"].includes(
    cleanSearchValue(equipo?.estado).normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase()
  );

  const getEstadoOptions = () => {
    const estadoActual = cleanSearchValue(equipo?.estado).toLowerCase();
    // Recepción de un equipo en Transición (misma regla que "Recibido" en Listado de Equipos): Inventario/Admin/Gerente lo reciben como
    // Almacén o Inactivo; el resto, solo como Inactivo. El backend valida además que el equipo sea de la sucursal de quien lo recibe.
    if (esTransicionActual) return canManageInventory ? ["Almacen", "Inactivo"] : ["Inactivo"];
    if (estadoActual === "almacen") return canManageInventory ? ["Inactivo", "Mantenimiento", "Baja"] : [];
    if (estadoActual === "inactivo") return canManageInventory ? ["Almacen", "Mantenimiento", "Baja"] : ["Mantenimiento", "Baja"];
    // Un equipo en Mantenimiento con cliente relacionado no se puede cambiar de estado
    // (el backend lo rechaza igual, pero acá evitamos que el botón quede habilitado).
    if (estadoActual === "mantenimiento") return clientes.length > 0 ? [] : ["Inactivo", "Baja"];
    // Desde Baja, cualquier usuario puede pasar a Inactivo o Robado (respetando sucursal,
    // como el resto de transiciones). Desde Robado, cualquier usuario puede volver a
    // Inactivo; reclasificar Robado->Baja queda solo para inventario/admin/gerente.
    if (estadoActual === "baja") return ["Inactivo", "Robado"];
    if (estadoActual === "robado") return canManageInventory ? ["Inactivo", "Baja"] : ["Inactivo"];
    return [];
  };

  const openDataDialog = (field, label, value) => {
    setDataDialog({ field, label });
    setDataValue(value || "");
    setPlacaConflicto(null);
    setDataDialogError("");
  };

  const saveDataField = async () => {
    if (!editableId || !detalleTipo || !dataDialog) return;
    try {
      setDataSaving(true);
      setDataDialogError("");
      setPlacaConflicto(null);
      if (dataDialog.field === "placa" && detalleTipo === "modem") {
        await actualizarPlacaModemCentro(editableId, dataValue);
      } else {
        await actualizarDatosEquipoCentro(detalleTipo, editableId, { [dataDialog.field]: dataValue });
      }
      setDataDialog(null);
      await loadDetalle({ tipo: detalleTipo, id: editableId, identificador: editableId });
      setToast({ open: true, message: "Datos actualizados", severity: "success" });
    } catch (e) {
      if (dataDialog.field === "placa" && e?.response?.data?.code === "REQUIERE_REASIGNACION") {
        setPlacaConflicto(dataValue);
      } else {
        setDataDialogError(e?.response?.data?.message || e?.message || "No se pudo actualizar el dato");
      }
    } finally {
      setDataSaving(false);
    }
  };

  const reasignarVehiculo = async () => {
    if (!editableId || !placaConflicto) return;
    try {
      setPlacaReasignando(true);
      setDataDialogError("");
      await reasignarVehiculoModemCentro(editableId, placaConflicto);
      setPlacaConflicto(null);
      setDataDialog(null);
      await loadDetalle({ tipo: detalleTipo, id: editableId, identificador: editableId });
      setToast({ open: true, message: "Módem reasignado al vehículo existente", severity: "success" });
    } catch (e) {
      setDataDialogError(e?.response?.data?.message || e?.message || "No se pudo reasignar el módem");
    } finally {
      setPlacaReasignando(false);
    }
  };

  const openEstadoDialog = () => {
    const options = getEstadoOptions();
    setEstadoValue(options[0] || "");
    setEstadoObs("");
    setEstadoDialogOpen(true);
  };

  const saveEstado = async () => {
    if (!editableId || !detalleTipo || !estadoValue) return;
    try {
      setEstadoSaving(true);
      setDetalleError("");
      await cambiarEstadoEquipoCentro(detalleTipo, editableId, estadoValue, estadoObs);
      setEstadoDialogOpen(false);
      await loadDetalle({ tipo: detalleTipo, id: editableId, identificador: editableId });
      setToast({ open: true, message: "Estado actualizado", severity: "success" });
    } catch (e) {
      setDetalleError(e?.response?.data?.message || e?.message || "No se pudo cambiar el estado");
    } finally {
      setEstadoSaving(false);
    }
  };

  const makeCopyButton = (value) => (
    <Button
      size="small"
      variant="text"
      startIcon={<ContentCopyIcon fontSize="small" />}
      onClick={() => copyText(value)}
      disabled={!value}
      sx={{ minWidth: 0, minHeight: 22, height: 22, px: 0.5, fontSize: 10 }}
    >
    </Button>
  );
  const editObsButton = (
    <Button
      size="small"
      variant="text"
      startIcon={<EditIcon fontSize="small" />}
      onClick={openObsDialog}
      disabled={!editableId}
      sx={{ minWidth: 0, minHeight: 22, height: 22, px: 0.5, fontSize: 10 }}
    >
      Editar
    </Button>
  );
  const makeEditDataButton = (field, label, value, extraDisabled = false) => (
    <Button
      size="small"
      variant="text"
      startIcon={<EditIcon fontSize="small" />}
      onClick={() => openDataDialog(field, label, value)}
      disabled={!editableId || extraDisabled}
      sx={{ minWidth: 0, minHeight: 22, height: 22, px: 0.5, fontSize: 10 }}
    >
      Editar
    </Button>
  );
  const editEstadoButton = (
    <Button
      size="small"
      variant="text"
      startIcon={<EditIcon fontSize="small" />}
      onClick={openEstadoDialog}
      disabled={!editableId || !getEstadoOptions().length}
      sx={{ minWidth: 0, minHeight: 22, height: 22, px: 0.5, fontSize: 10 }}
    >
      Editar
    </Button>
  );
  const withInlineActions = (fields = []) => fields.map((field) => {
    const label = String(field.label || "").toLowerCase();
    const isIdentifier = ["num sim:", "numero:", "serie:", "emai:", "email:"].includes(label);
    const isObservation = label.includes("observaci") || label.includes("observacion");
    if (label === "emai:" && detalleTipo === "chip") {
      return {
        ...field,
        action: (
          <Stack direction="row" spacing={0.25} alignItems="center">
            {makeCopyButton(field.value)}
            {makeEditDataButton("emai", "EMAI", field.value)}
          </Stack>
        ),
      };
    }
    if (isIdentifier) return { ...field, action: makeCopyButton(field.value) };
    if (isObservation) return { ...field, action: editObsButton };
    if (label === "alias:") return { ...field, action: makeEditDataButton("id_ref", "Alias", field.value) };
    if (label === "grupo:") return { ...field, action: makeEditDataButton("grupo", "Grupo", field.value) };
    if (label === "estado:") return { ...field, action: editEstadoButton };
    // El modelo se cambia eligiéndolo del catálogo (solo inventario, admin y gerente)
    if (label === "modelo:" && canManageInventory) {
      return {
        ...field,
        action: (
          <Button size="small" variant="text" startIcon={<EditIcon fontSize="small" />} onClick={() => setModeloDialogOpen(true)} disabled={!editableId}
            sx={{ minWidth: 0, minHeight: 22, height: 22, px: 0.5, fontSize: 10 }}>
            Editar
          </Button>
        ),
      };
    }
    if (label === "placa:" && detalleTipo === "modem") {
      // La placa solo se puede reasignar mientras el modem está en uso (Activo/Vendido
      // Activo) — en cualquier otro estado (Robado, Baja, Inactivo, etc.) queda bloqueada.
      const estadoModem = String(equipo?.estado || "").trim().toLowerCase();
      const placaEditable = ["activo", "vendido activo"].includes(estadoModem);
      return { ...field, action: makeEditDataButton("placa", "Placa", field.value, !placaEditable) };
    }
    return field;
  });
  const equipoFieldsWithActions = {
    left: withInlineActions(equipoFields.left),
    right: withInlineActions(equipoFields.right),
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 0.75,
        borderRadius: 1,
        bgcolor: "background.paper",
      }}
    >
      {/* Barra de búsqueda */}
      <Box
        sx={{
          display: hideSearch ? "none" : "flex",
          alignItems: "center",
          width: "100%",
          height: BAR_H,
          gap: 0,
          m: 0,
          p: 0,
        }}
      >
        <Select
          value={tipo}
          onChange={(e) => setTipo(e.target.value)}
          size="small"
          sx={{
            width: 128,
            height: BAR_H,
            flexShrink: 0,
            m: 0,
            fontSize: 12.5,
            borderRadius: "6px 0 0 6px",
            bgcolor: "background.paper",

            "& .MuiSelect-select": {
              height: BAR_H,
              minHeight: "unset !important",
              display: "flex",
              alignItems: "center",
              py: 0,
              px: "14px",
              boxSizing: "border-box",
            },

            "& .MuiOutlinedInput-notchedOutline": {
              borderRadius: "6px 0 0 6px",
            },
          }}
        >
          {TIPOS.map((t) => (
            <MenuItem key={t.value} value={t.value} sx={{ fontSize: 12.5 }}>
              {t.label}
            </MenuItem>
          ))}
        </Select>

        <TextField
          size="small"
          value={buscar}
          onChange={(e) => setBuscar(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") doSearch();
          }}
          placeholder="Buscar por identificador, número, placa..."
          slotProps={{
            input: {
              endAdornment: buscar ? (
                <InputAdornment position="end">
                  <IconButton size="small" edge="end" aria-label="Borrar búsqueda" onClick={limpiarBusqueda} sx={{ p: 0.25 }}>
                    <CloseIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            },
          }}
          sx={{
            flex: 1,
            minWidth: 0,
            m: 0,

            "& .MuiOutlinedInput-root": {
              height: BAR_H,
              fontSize: 12.5,
              bgcolor: (theme) =>
                theme.palette.mode === "dark"
                  ? alpha(theme.palette.primary.main, 0.12)
                  : "#eaf2ff",
              borderRadius: 0,
              display: "flex",
              alignItems: "center",

              "& fieldset": {
                borderLeft: "none",
                borderRadius: 0,
              },
            },

            "& .MuiInputBase-input": {
              height: BAR_H,
              py: 0,
              px: "10px",
              boxSizing: "border-box",
            },
          }}
        />

        <Button
          variant="contained"
          onClick={() => doSearch()}
          disabled={loading}
          disableElevation
          startIcon={
            loading ? (
              <CircularProgress size={13} color="inherit" />
            ) : (
              <SearchIcon sx={{ fontSize: "14px !important" }} />
            )
          }
          sx={{
            height: BAR_H,
            minHeight: BAR_H,
            m: 0,
            px: 1.75,
            flexShrink: 0,
            fontSize: 12,
            fontWeight: 800,
            borderRadius: "0 6px 6px 0",
            textTransform: "none",
            bgcolor: (theme) =>
              theme.palette.mode === "dark"
                ? theme.palette.primary.dark
                : "#24292f",

            "&:hover": {
              bgcolor: (theme) =>
                theme.palette.mode === "dark"
                  ? theme.palette.primary.main
                  : "#111820",
            },
          }}
        >
          Buscar
        </Button>
      </Box>

      {/* Progreso y errores */}
      {loading && <LinearProgress sx={{ mt: 0.75 }} />}

      {error && (
        <Alert severity="error" sx={{ mt: 0.75, py: 0, fontSize: 12 }}>
          {error}
        </Alert>
      )}

      {detalleError && (
        <Alert severity="error" sx={{ mt: 0.75, py: 0, fontSize: 12 }}>
          {detalleError}
        </Alert>
      )}

      {/* Similares */}
      <Stack
        direction="row"
        spacing={0.6}
        alignItems="center"
        flexWrap="wrap"
        sx={{
          display: hideSearch ? "none" : "flex",
          mt: 0.75,
          pt: 0.4,
          pb: 0.65,
          borderBottom: "1px solid",
          borderColor: "divider",
          minHeight: 26,
        }}
      >
        <Typography
          sx={{
            fontSize: 11.5,
            color: "text.secondary",
            flexShrink: 0,
          }}
        >
          Similares:
        </Typography>

        {rows.length ? (
          rows.map((row, index) => {
            const active =
              selected?.tipo === row.tipo &&
              String(normalizeId(selected)) === String(normalizeId(row));

            return (
              <Chip
                key={`${row.tipo}-${normalizeId(row)}-${index}`}
                size="small"
                icon={<SearchIcon sx={{ fontSize: "11px !important" }} />}
                label={getMain(row)}
                onClick={() => loadDetalle(row)}
                variant="outlined"
                sx={{
                  height: 20,
                  fontSize: 10.5,
                  fontWeight: 800,
                  cursor: "pointer",
                  bgcolor: (theme) =>
                    active
                      ? alpha(theme.palette.warning.main, theme.palette.mode === "dark" ? 0.18 : 0.24)
                      : theme.palette.background.paper,
                  borderColor: active ? "warning.main" : "divider",
                  color: active ? "warning.dark" : "text.primary",

                  "& .MuiChip-label": {
                    px: 0.75,
                  },

                  "& .MuiChip-icon": {
                    color: active ? "warning.main" : "text.secondary",
                    ml: "5px",
                  },
                }}
              />
            );
          })
        ) : (
          <Typography
            sx={{
              fontSize: 11.5,
              color: "text.secondary",
              fontStyle: "italic",
            }}
          >
            Sin resultados
          </Typography>
        )}
      </Stack>

      {/* Contenido principal */}
      <Box sx={{ pt: hideSearch ? 0 : 0.75 }}>
        {!detalle && !selected ? (
          <Typography
            sx={{
              fontSize: 12,
              color: "text.secondary",
              fontStyle: "italic",
              p: 1,
            }}
          >
            Ingresa un identificador y presiona Buscar.
          </Typography>
        ) : null}
        {!detalle && !selected && recientes.length > 0 && !hideSearch && (
          <Box sx={{ px: 1, pb: 1, display: "grid", gap: 0.6 }}>
            <Stack direction="row" alignItems="center" spacing={0.6}>
              <HistoryIcon sx={{ fontSize: 15, color: "text.secondary" }} />
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.4 }}>Últimas búsquedas</Typography>
              <Button size="small" onClick={limpiarRecientes} sx={{ textTransform: "none", fontSize: 11.5, minHeight: 0, py: 0, ml: "auto !important" }}>Borrar historial</Button>
            </Stack>
            <Box sx={{ display: "flex", gap: 0.6, flexWrap: "wrap" }}>
              {recientes.map((r) => (
                <Chip
                  key={`${r.tipo}|${r.buscar}`} size="small" variant="outlined" clickable onClick={() => repetirReciente(r)}
                  onDelete={() => quitarReciente(r)} deleteIcon={<CloseIcon />}
                  label={<span><b>{r.buscar}</b><span style={{ opacity: 0.65 }}>{` · ${(TIPOS.find((t) => t.value === r.tipo) || {}).label || r.tipo}`}</span></span>}
                  sx={{ height: 22, fontSize: 11.5 }}
                />
              ))}
            </Box>
          </Box>
        )}
        {!detalle && !selected ? (
          null
        ) : (
          <Stack spacing={0.9}>
            <Grid container spacing={0.9}>
              <Grid size={{ xs: 12, lg: 5 }}>
                <InfoBox
                  title={(selected?.tipo || tipo || "equipo").toUpperCase()}
                  theme={BOX_THEME.equipo}
                >
                  <FieldRows
                    left={equipoFieldsWithActions.left}
                    right={equipoFieldsWithActions.right}
                    labelSx={BOX_THEME.equipo.label}
                  />
                </InfoBox>
              </Grid>

              <Grid size={{ xs: 12, lg: 3 }}>
                <InfoBox title={relationTitle} theme={BOX_THEME.chip}>
                  {detalleTipo === "modem" ? (
                    <ChipsRelacionadosTable
                      chips={Array.isArray(chips) ? chips : []}
                      modem={equipo}
                      onToast={setToast}
                      onError={setDetalleError}
                      onSelectChip={setChipModalNumero}
                    />
                  ) : detalleTipo === "placa" ? (
                    <ModemsRelacionadosTable
                      modems={Array.isArray(modems) ? modems : []}
                      onSelectModem={setModemModalSerie}
                    />
                  ) : (
                    <FieldRows
                      left={relationFields}
                      labelSx={BOX_THEME.chip.label}
                    />
                  )}
                </InfoBox>
              </Grid>

              <Grid size={{ xs: 12, lg: 4 }}>
                <InfoBox
                  title="Cliente relacionado"
                  theme={BOX_THEME.cliente}
                >
                  <FieldRows
                    left={buildClienteFields(clientes)}
                    labelSx={BOX_THEME.cliente.label}
                  />
                </InfoBox>
              </Grid>
            </Grid>

            {detalleTipo === "modem" && (
              <ModemPlatformsPanel modem={equipo} onError={setDetalleError} />
            )}

            {detalleTipo === "chip" && (
              <ChipEmnifyPanel chip={equipo} onError={setDetalleError} />
            )}

            {detalleLoading ? (
              <LinearProgress />
            ) : detalleTipo === "modem" ? (
              <>
                <Tabs
                  value={historialTab}
                  onChange={(_, value) => setHistorialTab(value)}
                  sx={{ minHeight: 30, mb: 0.5, "& .MuiTab-root": { minHeight: 30, py: 0.4, fontSize: 11.5, textTransform: "none" } }}
                >
                  <Tab label="Historial modem" />
                  <Tab label={`Chips relacionados (${(detalle?.historialChips || []).length})`} />
                </Tabs>
                {historialTab === 0 ? (
                  <HistorialTable rows={detalle?.historial || []} tipo="modem" />
                ) : (
                  <HistorialTable rows={detalle?.historialChips || []} tipo="chip" />
                )}
              </>
            ) : (
              <HistorialTable rows={detalle?.historial || []} tipo={detalleTipo} />
            )}
          </Stack>
        )}
      </Box>

      <Dialog open={obsOpen} onClose={() => !obsSaving && setObsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: 14, fontWeight: 800, py: 1 }}>
          Editar observacion
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={4}
            value={obsValue}
            onChange={(e) => setObsValue(e.target.value)}
            inputProps={{ maxLength: 500 }}
            helperText={`${obsValue.length}/500`}
            sx={{ "& .MuiInputBase-input": { fontSize: 12 } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5 }}>
          <Button size="small" onClick={() => setObsOpen(false)} disabled={obsSaving}>
            Cancelar
          </Button>
          <Button size="small" variant="contained" onClick={saveObservacion} disabled={obsSaving || !editableId}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(dataDialog)}
        onClose={() => {
          if (dataSaving || placaReasignando) return;
          setDataDialog(null);
          setPlacaConflicto(null);
          setDataDialogError("");
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 14, fontWeight: 800, py: 1 }}>
          Editar {dataDialog?.label || "dato"}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {dataDialogError && (
            <Alert severity="error" sx={{ mb: 1.5, fontSize: 12 }}>
              {dataDialogError}
            </Alert>
          )}
          {placaConflicto && (
            <Alert severity="info" sx={{ mb: 1.5, fontSize: 12 }}>
              La placa <strong>{placaConflicto}</strong> ya está en uso por otro módem. Si continúas,
              se moverá <strong>solo este módem</strong> a esa placa — el otro módem no se ve afectado.
              <Box sx={{ mt: 1 }}>
                <Button
                  size="small"
                  variant="contained"
                  color="info"
                  onClick={reasignarVehiculo}
                  disabled={placaReasignando}
                >
                  {placaReasignando ? "Reasignando..." : `Reasignar este módem a ${placaConflicto}`}
                </Button>
              </Box>
            </Alert>
          )}
          <TextField
            autoFocus
            fullWidth
            value={dataValue}
            onChange={(e) => {
              setDataValue(e.target.value);
              setPlacaConflicto(null);
            }}
            inputProps={{ maxLength: 120 }}
            sx={{ "& .MuiInputBase-input": { fontSize: 12 } }}
            disabled={placaReasignando}
          />
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5 }}>
          <Button
            size="small"
            onClick={() => { setDataDialog(null); setPlacaConflicto(null); setDataDialogError(""); }}
            disabled={dataSaving || placaReasignando}
          >
            Cancelar
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={saveDataField}
            disabled={dataSaving || placaReasignando || !editableId}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <CambiarModeloDialog
        open={modeloDialogOpen}
        tipo={detalleTipo}
        equipoId={editableId}
        modeloActual={equipo?.modelo}
        marcaActual={equipo?.marca}
        onClose={() => setModeloDialogOpen(false)}
        onGuardado={async () => {
          setModeloDialogOpen(false);
          await loadDetalle({ tipo: detalleTipo, id: editableId, identificador: editableId });
          setToast({ open: true, message: "Modelo actualizado", severity: "success" });
        }}
      />

      <Dialog open={estadoDialogOpen} onClose={() => !estadoSaving && setEstadoDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: 14, fontWeight: 800, py: 1 }}>
          {esTransicionActual ? "Recibir equipo" : "Cambiar estado"}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Stack spacing={1}>
            {esTransicionActual && (
              <Alert severity="info" sx={{ py: 0, fontSize: 12 }}>
                {canManageInventory
                  ? "Elige si el equipo entra a Almacén o queda Inactivo en tu sucursal."
                  : "El equipo quedará Inactivo en tu sucursal."}
              </Alert>
            )}
            <Select
              size="small"
              value={estadoValue}
              onChange={(e) => setEstadoValue(e.target.value)}
              fullWidth
            >
              {getEstadoOptions().map((item) => (
                <MenuItem key={item} value={item}>{item}</MenuItem>
              ))}
            </Select>
            <TextField
              label="Observacion"
              value={estadoObs}
              onChange={(e) => setEstadoObs(e.target.value)}
              multiline
              minRows={3}
              inputProps={{ maxLength: 500 }}
              fullWidth
              size="small"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5 }}>
          <Button size="small" onClick={() => setEstadoDialogOpen(false)} disabled={estadoSaving}>
            Cancelar
          </Button>
          <Button size="small" variant="contained" onClick={saveEstado} disabled={estadoSaving || !estadoValue}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!modemModalSerie}
        onClose={() => setModemModalSerie(null)}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { borderRadius: 1.25, maxHeight: "92vh" } }}
      >
        <DialogTitle sx={{ py: 1, fontSize: "0.95rem", fontWeight: 900 }}>
          Detalle del modem
          <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>
            {modemModalSerie || "-"}
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 1, bgcolor: "#f8fafc" }}>
          {modemModalSerie && (
            <BusquedaPorIdentificador
              key={modemModalSerie}
              initialTipo="modem"
              initialBuscar={modemModalSerie}
              autoLoadKey={modemModalSerie}
              hideSearch
            />
          )}
        </DialogContent>
        <DialogActions sx={{ p: 0.75 }}>
          <Button size="small" onClick={() => setModemModalSerie(null)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!chipModalNumero}
        onClose={() => setChipModalNumero(null)}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { borderRadius: 1.25, maxHeight: "92vh" } }}
      >
        <DialogTitle sx={{ py: 1, fontSize: "0.95rem", fontWeight: 900 }}>
          Detalle del chip
          <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>
            {chipModalNumero || "-"}
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 1, bgcolor: "#f8fafc" }}>
          {chipModalNumero && (
            <BusquedaPorIdentificador
              key={chipModalNumero}
              initialTipo="chip"
              initialBuscar={chipModalNumero}
              autoLoadKey={chipModalNumero}
              hideSearch
            />
          )}
        </DialogContent>
        <DialogActions sx={{ p: 0.75 }}>
          <Button size="small" onClick={() => setChipModalNumero(null)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={2200}
        onClose={() => setToast((current) => ({ ...current, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          sx={{ py: 0.25, fontSize: 12 }}
          onClose={() => setToast((current) => ({ ...current, open: false }))}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
}
